//	Classic Perlin 3D Noise 
//	by Stefan Gustavson
//
const PerlinNoise3D = `
vec4 permute(vec4 x){return mod(((x*34.0)+1.0)*x, 289.0);}
vec4 taylorInvSqrt(vec4 r){return 1.79284291400159 - 0.85373472095314 * r;}
vec3 fade(vec3 t) {return t*t*t*(t*(t*6.0-15.0)+10.0);}

float cnoise(vec3 P){
  vec3 Pi0 = floor(P); // Integer part for indexing
  vec3 Pi1 = Pi0 + vec3(1.0); // Integer part + 1
  Pi0 = mod(Pi0, 289.0);
  Pi1 = mod(Pi1, 289.0);
  vec3 Pf0 = fract(P); // Fractional part for interpolation
  vec3 Pf1 = Pf0 - vec3(1.0); // Fractional part - 1.0
  vec4 ix = vec4(Pi0.x, Pi1.x, Pi0.x, Pi1.x);
  vec4 iy = vec4(Pi0.yy, Pi1.yy);
  vec4 iz0 = Pi0.zzzz;
  vec4 iz1 = Pi1.zzzz;

  vec4 ixy = permute(permute(ix) + iy);
  vec4 ixy0 = permute(ixy + iz0);
  vec4 ixy1 = permute(ixy + iz1);

  vec4 gx0 = ixy0 / 7.0;
  vec4 gy0 = fract(floor(gx0) / 7.0) - 0.5;
  gx0 = fract(gx0);
  vec4 gz0 = vec4(0.5) - abs(gx0) - abs(gy0);
  vec4 sz0 = step(gz0, vec4(0.0));
  gx0 -= sz0 * (step(0.0, gx0) - 0.5);
  gy0 -= sz0 * (step(0.0, gy0) - 0.5);

  vec4 gx1 = ixy1 / 7.0;
  vec4 gy1 = fract(floor(gx1) / 7.0) - 0.5;
  gx1 = fract(gx1);
  vec4 gz1 = vec4(0.5) - abs(gx1) - abs(gy1);
  vec4 sz1 = step(gz1, vec4(0.0));
  gx1 -= sz1 * (step(0.0, gx1) - 0.5);
  gy1 -= sz1 * (step(0.0, gy1) - 0.5);

  vec3 g000 = vec3(gx0.x,gy0.x,gz0.x);
  vec3 g100 = vec3(gx0.y,gy0.y,gz0.y);
  vec3 g010 = vec3(gx0.z,gy0.z,gz0.z);
  vec3 g110 = vec3(gx0.w,gy0.w,gz0.w);
  vec3 g001 = vec3(gx1.x,gy1.x,gz1.x);
  vec3 g101 = vec3(gx1.y,gy1.y,gz1.y);
  vec3 g011 = vec3(gx1.z,gy1.z,gz1.z);
  vec3 g111 = vec3(gx1.w,gy1.w,gz1.w);

  vec4 norm0 = taylorInvSqrt(vec4(dot(g000, g000), dot(g010, g010), dot(g100, g100), dot(g110, g110)));
  g000 *= norm0.x;
  g010 *= norm0.y;
  g100 *= norm0.z;
  g110 *= norm0.w;
  vec4 norm1 = taylorInvSqrt(vec4(dot(g001, g001), dot(g011, g011), dot(g101, g101), dot(g111, g111)));
  g001 *= norm1.x;
  g011 *= norm1.y;
  g101 *= norm1.z;
  g111 *= norm1.w;

  float n000 = dot(g000, Pf0);
  float n100 = dot(g100, vec3(Pf1.x, Pf0.yz));
  float n010 = dot(g010, vec3(Pf0.x, Pf1.y, Pf0.z));
  float n110 = dot(g110, vec3(Pf1.xy, Pf0.z));
  float n001 = dot(g001, vec3(Pf0.xy, Pf1.z));
  float n101 = dot(g101, vec3(Pf1.x, Pf0.y, Pf1.z));
  float n011 = dot(g011, vec3(Pf0.x, Pf1.yz));
  float n111 = dot(g111, Pf1);

  vec3 fade_xyz = fade(Pf0);
  vec4 n_z = mix(vec4(n000, n100, n010, n110), vec4(n001, n101, n011, n111), fade_xyz.z);
  vec2 n_yz = mix(n_z.xy, n_z.zw, fade_xyz.y);
  float n_xyz = mix(n_yz.x, n_yz.y, fade_xyz.x); 
  return 2.2 * n_xyz;
}
`

// From the book: Graphics Shaders
const CMYK = `
vec3 CMYKtoRGB (vec4 cmyk) {
    float c = cmyk.x;
    float m = cmyk.y;
    float y = cmyk.z;
    float k = cmyk.w;

    float invK = 1.0 - k;
    float r = 1.0 - min(1.0, c * invK + k);
    float g = 1.0 - min(1.0, m * invK + k);
    float b = 1.0 - min(1.0, y * invK + k);
    return clamp(vec3(r, g, b), 0.0, 1.0);
}

vec4 RGBtoCMYK (vec3 rgb) {
    float r = rgb.r;
    float g = rgb.g;
    float b = rgb.b;
    float k = min(1.0 - r, min(1.0 - g, 1.0 - b));
    vec3 cmy = vec3(0.0);
    float invK = 1.0 - k;
    if (invK != 0.0) {
        cmy.x = (1.0 - r - k) / invK;
        cmy.y = (1.0 - g - k) / invK;
        cmy.z = (1.0 - b - k) / invK;
    }
    return clamp(vec4(cmy, k), 0.0, 1.0);
}
`

export const pixelsWithNoise = `
precision mediump float;
varying vec2 v_position;

uniform int u_state;
uniform int u_time;
uniform int u_anchorTime;
uniform vec2 u_canvasSize;
uniform vec2 u_cursorPosition;
uniform vec2 u_particlePosition;

uniform sampler2D u_sampler0;
uniform sampler2D u_layoutSampler;

${PerlinNoise3D}

void main() {
    float distToParticle;
    vec2 currentPixel, newPosition, samplingPosition;
    int stateChangeTime = u_time - u_anchorTime;

    if (u_state == 2) {
        float tDist = smoothstep(0., 2000., float(stateChangeTime)) / 50.;
        float distMult = 1./tDist;
    
        float tZoom = pow(smoothstep(0., 6000., float(stateChangeTime)), 1.5);
        float zoomMult = 1./tZoom;
    
        vec2 center = vec2(0.5, 0.5);
        vec2 toCenter = center - v_position;
        float distToCentre = length(toCenter);
        newPosition = v_position + (toCenter * ((distToCentre + 1.) / zoomMult));
        samplingPosition = v_position + (toCenter * (distToCentre / distMult));
        
        distToParticle = distance(newPosition.xy * u_canvasSize, u_cursorPosition) * 2. / min(u_canvasSize.x, u_canvasSize.y);
        currentPixel = newPosition.xy * u_canvasSize;
    } else {
        newPosition = v_position;
        distToParticle = distance(v_position.xy * u_canvasSize, u_cursorPosition) * 2. / min(u_canvasSize.x, u_canvasSize.y);
        currentPixel = v_position.xy * u_canvasSize;
    }
    float tFadeOut = smoothstep(5000., 4000., float(stateChangeTime));
    distToParticle = clamp(distToParticle * distToParticle * 4., 0., 1.);

    float pixelSizeX = 6.;
    float pixelSizeY = 6.;
    float pixelBorderX = 1.;

    float bgBrightnessMult = 0.8;
    float noiseScale = 6.;
    float noiseSpeedMult = 3000.;

    float canvasBorderX = ceil(mod(u_canvasSize.x, (pixelSizeX + pixelBorderX)) / 2.0);
    float canvasBorderY = ceil(mod(u_canvasSize.y, pixelSizeY) / 2.0);

    float r = 1. - step(pixelSizeY / 3., mod(currentPixel.y - canvasBorderY, pixelSizeY));
    float g = 1. - step(pixelSizeY / 3., mod(currentPixel.y - canvasBorderY - pixelSizeY / 3., pixelSizeY));
    float b = 1. - step(pixelSizeY / 3., mod(currentPixel.y - canvasBorderY - pixelSizeY / 3. * 2., pixelSizeY));
    float pixelsX = 1. - step(pixelSizeX, mod(currentPixel.x - canvasBorderX, (pixelSizeX + pixelBorderX)));
    float pixelsXBorder = min(step(canvasBorderX, currentPixel.x), 1. - step(u_canvasSize.x - canvasBorderX, currentPixel.x));
    pixelsX = min(pixelsX, pixelsXBorder);
    float pixelsYBorder = min(step(canvasBorderY, currentPixel.y), 1. - step(u_canvasSize.y - canvasBorderY, currentPixel.y));
    float pixels =  min(pixelsX, pixelsYBorder);

    float pixelNumX = floor((currentPixel.x - canvasBorderX) / (pixelSizeX + pixelBorderX));
    float pixelNumY = floor((currentPixel.y - canvasBorderY) / pixelSizeY);
    vec2 pixelCenter = vec2(pixelNumX * (pixelSizeX + pixelBorderX) + pixelSizeX / 2. + canvasBorderX, pixelNumY * pixelSizeX + pixelSizeX / 2. + canvasBorderY);
    float pixelNoise1 = step(0.0, cnoise(vec3((pixelCenter - newPosition) / 1., float(u_time)/10000.)) ) * 0.15 + 0.85;
    float pixelNoise2 = step(0.0, cnoise(vec3((pixelCenter - newPosition) / 8., float(u_time)/5000.)) ) * 0.15 + 0.85;

    float proportion = u_canvasSize.x / u_canvasSize.y;
    vec2 proportionRelatedPosition;
    vec2 centerOffset;
    if (proportion < 1.) {
        proportionRelatedPosition = vec2(newPosition.x, newPosition.y / proportion);
        centerOffset = vec2(0., (1. - 1. / proportion) / 2.);
    } else {
        proportionRelatedPosition = vec2(newPosition.x * proportion, newPosition.y);
        centerOffset = vec2((1. - proportion) / 2., 0.);
    }

    float noiseMapR, noiseMapG, noiseMapB;
    float waveR, waveG, waveB;
    if (u_state == 0 || u_state == 1) {
        float waveSpeed = float(stateChangeTime) / 2000.;
        float distToCenter = distance(proportionRelatedPosition.xy + centerOffset, vec2(.5, .5));
        float timeFader = smoothstep(1., 0.5, distToCenter + 1. - waveSpeed);
        timeFader = timeFader * smoothstep(-0.2, 0.5, distToCenter + 1. - waveSpeed);
        waveR = 1. - pow(1. - sin((distToCenter - waveSpeed - (0.08 * smoothstep(0., 1., distToCenter) )) * 25.) * timeFader, 3.);
        waveG = 1. - pow(1. - sin((distToCenter - waveSpeed - (0.04 * smoothstep(0., 1., distToCenter) )) * 25.) * timeFader, 3.);
        waveB = 1. - pow(1. - sin((distToCenter - waveSpeed) * 25.) * timeFader, 3.);

        timeFader = smoothstep(0.0, -0.7, distToCenter + 1. - waveSpeed);
        noiseMapR = cnoise(vec3(proportionRelatedPosition * noiseScale, float(u_time)/noiseSpeedMult) + 0.2 * distToParticle) * timeFader;
        noiseMapG = cnoise(vec3(proportionRelatedPosition * noiseScale, float(u_time)/noiseSpeedMult) + 0.1 * distToParticle) * timeFader;
        noiseMapB = cnoise(vec3(proportionRelatedPosition * noiseScale, float(u_time)/noiseSpeedMult)) * timeFader;

        float noiseWaveR = max(waveR, noiseMapR);
        float noiseWaveG = max(waveG, noiseMapG);
        float noiseWaveB = max(waveB, noiseMapB);
        
        vec4 sample = texture2D(u_layoutSampler, v_position);
        float sr = min(r * sample.r * pixelNoise1 * pixelNoise2, pixels);
        float sg = min(g * sample.g * pixelNoise1 * pixelNoise2, pixels);
        float sb = min(b * sample.b * pixelNoise1 * pixelNoise2, pixels);
        
        r = min(r * noiseWaveR * pixelNoise1 * pixelNoise2, pixels);
        g = min(g * noiseWaveG * pixelNoise1 * pixelNoise2, pixels);
        b = min(b * noiseWaveB * pixelNoise1 * pixelNoise2, pixels);
        
        gl_FragColor = max(vec4(vec3(r, g, b) * bgBrightnessMult, 1.0), vec4(vec3(sr, sg, sb), 1.0));
    } else {
        noiseMapR = cnoise(vec3(proportionRelatedPosition * noiseScale, float(u_time)/noiseSpeedMult) + 0.2 * distToParticle);
        noiseMapG = cnoise(vec3(proportionRelatedPosition * noiseScale, float(u_time)/noiseSpeedMult) + 0.1 * distToParticle);
        noiseMapB = cnoise(vec3(proportionRelatedPosition * noiseScale, float(u_time)/noiseSpeedMult));

        float tBrightness = min(smoothstep(0., 2500., float(stateChangeTime)) * 0.5 + 1., smoothstep(4500., 2500., float(stateChangeTime)) * 1.5);
        bgBrightnessMult = bgBrightnessMult * tBrightness;

        vec4 sample = texture2D(u_sampler0, samplingPosition);
        vec4 sampler = vec4(sample.r * 0.9, sample.g * 0.9 + sample.r * 0.1, sample.b * 0.9 + sample.g * 0.1, 1.);

        r = min(r * noiseMapR * pixelNoise1 * pixelNoise2, pixels);
        g = min(g * noiseMapG * pixelNoise1 * pixelNoise2, pixels);
        b = min(b * noiseMapB * pixelNoise1 * pixelNoise2, pixels);

        gl_FragColor = max(vec4(vec3(r, g, b) * bgBrightnessMult, 1.0), sampler) * vec4(tFadeOut, tFadeOut, tFadeOut, 1.);
    }
}
`

export const glitchPixelsWithNoise = `
precision mediump float;
varying vec2 v_position;

uniform int u_state;
uniform int u_time;
uniform int u_anchorTime;
uniform vec2 u_canvasSize;
uniform vec2 u_cursorPosition;
uniform vec2 u_particlePosition;

uniform sampler2D u_layoutSampler;
uniform sampler2D u_sampler0;
uniform sampler2D u_sampler1;

uniform float u_param0;

${PerlinNoise3D}

void main() {
    float distToParticle;
    vec2 currentPixel, newPosition, samplingPosition;
    int stateChangeTime = u_time - u_anchorTime;

    if (u_state == 2) {
        float tDist = smoothstep(0., 2000., float(stateChangeTime)) / 50.;
        float distMult = 1./tDist;
    
        float tZoom = pow(smoothstep(0., 6000., float(stateChangeTime)), 1.5);
        float zoomMult = 1./tZoom;
    
        vec2 center = vec2(0.5, 0.5);
        vec2 toCenter = center - v_position;
        float distToCentre = length(toCenter);
        newPosition = v_position + (toCenter * ((distToCentre + 1.) / zoomMult));
        samplingPosition = v_position + (toCenter * (distToCentre / distMult));
        
        distToParticle = distance(newPosition.xy * u_canvasSize, u_cursorPosition) * 2. / min(u_canvasSize.x, u_canvasSize.y);
        currentPixel = newPosition.xy * u_canvasSize;
    } else {
        newPosition = v_position;
        distToParticle = distance(v_position.xy * u_canvasSize, u_cursorPosition) * 2. / min(u_canvasSize.x, u_canvasSize.y);
        currentPixel = v_position.xy * u_canvasSize;
    }
    float tFadeOut = smoothstep(5000., 4000., float(stateChangeTime));
    distToParticle = clamp(distToParticle * distToParticle * 4., 0., 1.);

    float gridSize = mix(3., 17.9, u_param0);
    float pixelSizeX = gridSize - mod(gridSize, 3.);
    float pixelSizeY = gridSize - mod(gridSize, 3.);
    float pixelBorderX = 1.;

    float bgBrightnessMult = 0.8;
    float noiseScale = 6.;
    float noiseSpeedMult = 3000.;

    float canvasBorderX = ceil(mod(u_canvasSize.x, (pixelSizeX + pixelBorderX)) / 2.0);
    float canvasBorderY = ceil(mod(u_canvasSize.y, pixelSizeY) / 2.0);

    float r = 1. - step(pixelSizeY / 3., mod(currentPixel.y - canvasBorderY, pixelSizeY));
    float g = 1. - step(pixelSizeY / 3., mod(currentPixel.y - canvasBorderY - pixelSizeY / 3., pixelSizeY));
    float b = 1. - step(pixelSizeY / 3., mod(currentPixel.y - canvasBorderY - pixelSizeY / 3. * 2., pixelSizeY));
    float pixelsX = 1. - step(pixelSizeX, mod(currentPixel.x - canvasBorderX, (pixelSizeX + pixelBorderX)));
    float pixelsXBorder = min(step(canvasBorderX, currentPixel.x), 1. - step(u_canvasSize.x - canvasBorderX, currentPixel.x));
    pixelsX = min(pixelsX, pixelsXBorder);
    float pixelsYBorder = min(step(canvasBorderY, currentPixel.y), 1. - step(u_canvasSize.y - canvasBorderY, currentPixel.y));
    float pixels =  min(pixelsX, pixelsYBorder);

    float pixelNumX = floor((currentPixel.x - canvasBorderX) / (pixelSizeX + pixelBorderX));
    float pixelNumY = floor((currentPixel.y - canvasBorderY) / pixelSizeY);
    vec2 pixelCenter = vec2(pixelNumX * (pixelSizeX + pixelBorderX) + pixelSizeX / 2. + canvasBorderX, pixelNumY * pixelSizeX + pixelSizeX / 2. + canvasBorderY);
    float pixelNoise1 = step(0.0, cnoise(vec3((pixelCenter - newPosition) / 1., float(u_time)/10000.)) ) * 0.15 + 0.85;
    float pixelNoise2 = step(0.0, cnoise(vec3((pixelCenter - newPosition) / 8., float(u_time)/5000.)) ) * 0.15 + 0.85;

    float proportion = u_canvasSize.x / u_canvasSize.y;
    vec2 proportionRelatedPosition;
    vec2 centerOffset;
    if (proportion < 1.) {
        proportionRelatedPosition = vec2(newPosition.x, newPosition.y / proportion);
        centerOffset = vec2(0., (1. - 1. / proportion) / 2.);
    } else {
        proportionRelatedPosition = vec2(newPosition.x * proportion, newPosition.y);
        centerOffset = vec2((1. - proportion) / 2., 0.);
    }

    float noiseMapR, noiseMapG, noiseMapB;
    float waveR, waveG, waveB;
    float abberationMult = smoothstep(0., 1.,(sin(smoothstep(0., 1., v_position.y * 2. - 0.6) * 3.1415926535897932384626433832795) * pow(sin(v_position.y/10. + float(u_time)/1000.), 3.) - 0.5) * 2.);
    float abberationMultSec = smoothstep(0., 1.,(sin(smoothstep(0., 1., ( mod(v_position.y - float(u_time)/2000., 1.) ) * 2. - 0.8) * 3.1415926535897932384626433832795)));

    vec2 texRCoord = vec2(pixelCenter.x, pixelCenter.y - (pixelSizeY / 3.)) / u_canvasSize;
    vec2 texGCoord = pixelCenter / u_canvasSize;
    vec2 texBCoord = vec2(pixelCenter.x, pixelCenter.y + (pixelSizeY / 3.)) / u_canvasSize;

    vec4 sampleR = texture2D(u_sampler0, vec2(texRCoord.x + 0.005 + (sin(texRCoord.y * 400. + float(u_time)/20.) * cnoise(vec3(texRCoord.x, texRCoord.y * 20., 0.)) * 10. * 0.08 * abberationMult) + (sin(texRCoord.y * 500. + float(u_time)/20.) * 0.015 * abberationMultSec) + (smoothstep(0., 1., sin((texRCoord.y * 2. - float(u_time)/2000.) * 3.1415926535897932384626433832795)) * 0.01), texRCoord.y));
    vec4 sampleG = texture2D(u_sampler0, vec2(texGCoord.x + sin(texGCoord.y * 400. + float(u_time)/80.) * cnoise(vec3(texGCoord.x, texGCoord.y * 20., 0.)) * 10. * 0.04 * abberationMult + (sin(texGCoord.y * 500. + float(u_time)/20.) * 0.01 * abberationMultSec), texGCoord.y));
    vec4 sampleB = texture2D(u_sampler0, vec2(texBCoord.x - 0.005 - (sin(texBCoord.y * 400. + float(u_time)/40.) * cnoise(vec3(texBCoord.x, texBCoord.y * 20., 0.))* 10. * 0.08 * abberationMult) + (sin(texBCoord.y * 500. + float(u_time)/40.) * 0.015 * abberationMultSec) - (smoothstep(0., 1., sin((texBCoord.y * 2. - float(u_time)/2000.) * 3.1415926535897932384626433832795)) * 0.01), texBCoord.y));

    if (u_state == 0) {
        float waveSpeed = float(stateChangeTime) / 2000.;
        float distToCenter = distance(proportionRelatedPosition.xy + centerOffset, vec2(.5, .5));
        float timeFader = smoothstep(1., 0.5, distToCenter + 1. - waveSpeed);
        timeFader = timeFader * smoothstep(-0.2, 0.5, distToCenter + 1. - waveSpeed);
        waveR = 1. - pow(1. - sin((distToCenter - waveSpeed - (0.08 * smoothstep(0., 1., distToCenter) )) * 25.) * timeFader, 3.);
        waveG = 1. - pow(1. - sin((distToCenter - waveSpeed - (0.04 * smoothstep(0., 1., distToCenter) )) * 25.) * timeFader, 3.);
        waveB = 1. - pow(1. - sin((distToCenter - waveSpeed) * 25.) * timeFader, 3.);

        timeFader = smoothstep(0.0, -0.7, distToCenter + 1. - waveSpeed);
        noiseMapR = cnoise(vec3(proportionRelatedPosition * noiseScale, float(u_time)/noiseSpeedMult) + 0.2 * distToParticle) * timeFader;
        noiseMapG = cnoise(vec3(proportionRelatedPosition * noiseScale, float(u_time)/noiseSpeedMult) + 0.1 * distToParticle) * timeFader;
        noiseMapB = cnoise(vec3(proportionRelatedPosition * noiseScale, float(u_time)/noiseSpeedMult)) * timeFader;

        float noiseWaveR = max(waveR, noiseMapR);
        float noiseWaveG = max(waveG, noiseMapG);
        float noiseWaveB = max(waveB, noiseMapB);
        
        vec4 sample = texture2D(u_layoutSampler, v_position);
        float sr = min(r * sample.r * pixelNoise1 * pixelNoise2, pixels);
        float sg = min(g * sample.g * pixelNoise1 * pixelNoise2, pixels);
        float sb = min(b * sample.b * pixelNoise1 * pixelNoise2, pixels);
        
        sr = min(r * sampleR.b * pixelNoise1 * pixelNoise2 * 1.2, pixels) + sr;
        sg = min(g * sampleG.r * pixelNoise1 * pixelNoise2 * 1.2, pixels) + sg;
        sb = min(b * sampleB.g * pixelNoise1 * pixelNoise2 * 1.2, pixels) + sb;
        
        r = min(r * noiseWaveR * pixelNoise1 * pixelNoise2, pixels);
        g = min(g * noiseWaveG * pixelNoise1 * pixelNoise2, pixels);
        b = min(b * noiseWaveB * pixelNoise1 * pixelNoise2, pixels);
        
        gl_FragColor = vec4(vec3(r, g, b) * bgBrightnessMult, 1.0) + vec4(vec3(sr, sg, sb) * timeFader, 1.0);
    } else if(u_state == 1) {
        noiseMapR = cnoise(vec3(proportionRelatedPosition * noiseScale, float(u_time)/noiseSpeedMult) + 0.2 * distToParticle);
        noiseMapG = cnoise(vec3(proportionRelatedPosition * noiseScale, float(u_time)/noiseSpeedMult) + 0.1 * distToParticle);
        noiseMapB = cnoise(vec3(proportionRelatedPosition * noiseScale, float(u_time)/noiseSpeedMult));
        
        vec4 sample = texture2D(u_layoutSampler, v_position);
        float sr = min(r * sample.r * pixelNoise1 * pixelNoise2, pixels);
        float sg = min(g * sample.g * pixelNoise1 * pixelNoise2, pixels);
        float sb = min(b * sample.b * pixelNoise1 * pixelNoise2, pixels);
        
        sr = min(r * sampleR.b * pixelNoise1 * pixelNoise2 * 1.2, pixels) + sr;
        sg = min(g * sampleG.r * pixelNoise1 * pixelNoise2 * 1.2, pixels) + sg;
        sb = min(b * sampleB.g * pixelNoise1 * pixelNoise2 * 1.2, pixels) + sb;
        
        r = min(r * noiseMapR * pixelNoise1 * pixelNoise2, pixels);
        g = min(g * noiseMapG * pixelNoise1 * pixelNoise2, pixels);
        b = min(b * noiseMapB * pixelNoise1 * pixelNoise2, pixels);
        
        gl_FragColor = vec4(vec3(r, g, b) * bgBrightnessMult, 1.0) + vec4(vec3(sr, sg, sb), 1.0);
    } else {
        float sr = min(r * sampleR.b * pixelNoise1 * pixelNoise2 * 1.2, pixels);
        float sg = min(g * sampleG.r * pixelNoise1 * pixelNoise2 * 1.2, pixels);
        float sb = min(b * sampleB.g * pixelNoise1 * pixelNoise2 * 1.2, pixels);

        noiseMapR = cnoise(vec3(proportionRelatedPosition * noiseScale, float(u_time)/noiseSpeedMult) + 0.2 * distToParticle);
        noiseMapG = cnoise(vec3(proportionRelatedPosition * noiseScale, float(u_time)/noiseSpeedMult) + 0.1 * distToParticle);
        noiseMapB = cnoise(vec3(proportionRelatedPosition * noiseScale, float(u_time)/noiseSpeedMult));

        float tBrightness = min(smoothstep(0., 2500., float(stateChangeTime)) * 0.5 + 1., smoothstep(4500., 2500., float(stateChangeTime)) * 1.5);
        bgBrightnessMult = bgBrightnessMult * tBrightness;

        vec4 sample = texture2D(u_sampler1, samplingPosition);
        vec4 sampler = vec4(sample.r * 0.9, sample.g * 0.9 + sample.r * 0.1, sample.b * 0.9 + sample.g * 0.1, 1.);

        r = min(r * noiseMapR * pixelNoise1 * pixelNoise2, pixels);
        g = min(g * noiseMapG * pixelNoise1 * pixelNoise2, pixels);
        b = min(b * noiseMapB * pixelNoise1 * pixelNoise2, pixels);

        gl_FragColor = max(vec4(vec3(r, g, b) * bgBrightnessMult, 1.0) + vec4(vec3(sr, sg, sb) * bgBrightnessMult, 1.0), sampler) * vec4(tFadeOut, tFadeOut, tFadeOut, 1.);
    }
}
`

export const particleWithAbberationFs = `
precision mediump float;
varying vec2 v_position;

uniform int u_time;
uniform vec2 u_canvasSize;
uniform vec2 u_cursorPosition;
uniform vec2 u_particlePosition;

uniform sampler2D u_sampler0;

uniform float u_param0;

${PerlinNoise3D}

void main() {
    float gridSize = mix(3., 17.9, u_param0);
    float pixelSizeX = gridSize - mod(gridSize, 3.);
    float pixelSizeY = gridSize - mod(gridSize, 3.);

    float pixelBorderX = 1.;

    float noiseScale = 5.;
    float noiseSpeedMult = 2000.;

    float pBrightnessMult = 0.25;

    float canvasBorderX = ceil(mod(u_canvasSize.x, (pixelSizeX + pixelBorderX)) / 2.0);
    float canvasBorderY = ceil(mod(u_canvasSize.y, pixelSizeY) / 2.0);

    vec2 currentPixel = v_position.xy * u_canvasSize;
    float r = 1. - step(pixelSizeY / 3., mod(currentPixel.y - canvasBorderY, pixelSizeY));
    float g = 1. - step(pixelSizeY / 3., mod(currentPixel.y - canvasBorderY - pixelSizeY / 3., pixelSizeY));
    float b = 1. - step(pixelSizeY / 3., mod(currentPixel.y - canvasBorderY - pixelSizeY / 3. * 2., pixelSizeY));
    float pixelsX = 1. - step(pixelSizeX, mod(currentPixel.x - canvasBorderX, (pixelSizeX + pixelBorderX)));
    float pixelsXBorder = min(step(canvasBorderX, currentPixel.x), 1. - step(u_canvasSize.x - canvasBorderX, currentPixel.x));
    pixelsX = min(pixelsX, pixelsXBorder);
    float pixelsYBorder = min(step(canvasBorderY, currentPixel.y), 1. - step(u_canvasSize.y - canvasBorderY, currentPixel.y));
    float pixels =  min(pixelsX, pixelsYBorder);

    float pixelNumX = floor((currentPixel.x - canvasBorderX) / (pixelSizeX + pixelBorderX));
    float pixelNumY = floor((currentPixel.y - canvasBorderY) / pixelSizeY);
    vec2 pixelCenter = vec2(pixelNumX * (pixelSizeX + pixelBorderX) + pixelSizeX / 2. + canvasBorderX, pixelNumY * pixelSizeX + pixelSizeX / 2. + canvasBorderY);

    float distToParticle = distance(pixelCenter, u_particlePosition);
    float particle = 1. - smoothstep(0., 30., distToParticle);
    particle = particle * step(0.2, cnoise(vec3((pixelCenter - u_particlePosition / 10.) / noiseScale, float(u_time)/noiseSpeedMult)));

    float cursor = 1. - step(pixelSizeX, distToParticle);

    vec2 texRCoord = vec2(pixelCenter.x, pixelCenter.y - (pixelSizeY / 3.)) / u_canvasSize;
    vec2 texGCoord = pixelCenter / u_canvasSize;
    vec2 texBCoord = vec2(pixelCenter.x, pixelCenter.y + (pixelSizeY / 3.)) / u_canvasSize;

    vec4 texR = texture2D(u_sampler0, texRCoord);
    vec4 texG = texture2D(u_sampler0, texGCoord);
    vec4 texB = texture2D(u_sampler0, texBCoord);
    vec4 sampler = vec4(texR.r * 0.8, texG.g * 0.8 + texR.r * 0.2, texB.b * 0.8 + texG.g * 0.2, 1.);

    float rf = min(r * sampler.r - 0.01, pixels);
    float gf = min(g * sampler.g - 0.01, pixels);
    float bf = min(b * sampler.b - 0.01, pixels);
    vec4 fColor = vec4(vec3(rf, gf, bf), 1.);

    float rp = min(r * particle, pixels);
    float gp = min(g * particle, pixels);
    float bp = min(b * particle, pixels);
    vec4 pColor = vec4(vec3(rp, 0, 0) * pBrightnessMult, rp);

    float rc = min(r * cursor, pixels);
    float gc = min(g * cursor, pixels);
    float bc = min(b * cursor, pixels);
    vec4 cColor = vec4(vec3(rc, 0, 0), rc);

    gl_FragColor = max(pColor, cColor) + fColor;
    //gl_FragColor = tex;
}
`

export const mirrorCursorWithAbberationFs = `
precision mediump float;
varying vec2 v_position;

uniform int u_time;
uniform vec2 u_canvasSize;
uniform vec2 u_cursorPosition;
uniform vec2 u_particlePosition;

uniform sampler2D u_sampler0;

void main() {
    float pBrightnessMult = 0.3;

    float distToParticle = distance(v_position * u_canvasSize, u_particlePosition);
    distToParticle = distToParticle + ((sin(distToParticle - (float(u_time)/ 150.)) + 1.) / 2.) * 10.;
    float particle = (1. - smoothstep(0., 30., distToParticle));

    vec4 texture = texture2D(u_sampler0, v_position);
    vec4 sampler = vec4(texture.r * 0.8, texture.g * 0.8 + texture.r * 0.2, texture.b * 0.8 + texture.g * 0.2, 1.);

    vec4 fColor = vec4(sampler.rgb - 0.01, 1.);

    vec4 pColor = vec4(vec3(particle, 0, 0) * pBrightnessMult, particle);

    gl_FragColor = pColor + fColor;
}
`

export const CirclesNoiseFs = `
precision mediump float;
varying vec2 v_position;

uniform int u_state;
uniform int u_time;
uniform int u_anchorTime;

uniform vec2 u_canvasSize;
uniform vec2 u_cursorPosition;
uniform vec2 u_particlePosition;
uniform float u_pointerPressure;

uniform float u_param0;
uniform float u_param1;
uniform float u_param2;
uniform float u_param3;
uniform float u_param4;
uniform float u_param5;

uniform sampler2D u_sampler0;
uniform sampler2D u_layoutSampler;

${PerlinNoise3D}
${CMYK}

vec2 rotate2D(vec2 _st, float _angle){
    _st -= 0.5;
    _st =  mat2(cos(_angle),-sin(_angle),
                sin(_angle),cos(_angle)) * _st;
    _st += 0.5;
    return _st;
}

float circle(in vec2 _st, in float _radius, float _smoothEdges){
    vec2 l = _st-vec2(0.5);
    return 1.-smoothstep(_radius-(_radius*_smoothEdges),
                         _radius,
                         dot(l,l)*4.0);
}

void main() {
    int stateChangeTime = u_time - u_anchorTime;
    float distToCursor = distance(v_position.xy * u_canvasSize, u_cursorPosition) * 2. / min(u_canvasSize.x, u_canvasSize.y);

    float fadeInTime = 1000.;
    float fadeOutTime = 1000.;
    float bgBrightnessMult = 0.8;
    float noiseScale = 6.;
    float noiseSpeedMult = 3000.;
    
    float gridSize = mix(4., 20., u_param0);
    float gridRotationSpeed = mix(0., 10., u_param1);
    
    vec2 currentPixel = v_position.xy * u_canvasSize;
    float proportion = u_canvasSize.x / u_canvasSize.y;
    vec2 proportionRelatedPosition;
    vec2 centerOffset;
    if (proportion < 1.) {
        proportionRelatedPosition = vec2(v_position.x, v_position.y / proportion);
        centerOffset = vec2(0., (1. - 1. / proportion) / 2.);
    } else {
        proportionRelatedPosition = vec2(v_position.x * proportion, v_position.y);
        centerOffset = vec2((1. - proportion) / 2., 0.);
    }
    
    float distToCenter = distance(proportionRelatedPosition.xy + centerOffset, vec2(.5, .5));
    float fadeIn = smoothstep(0., fadeInTime + distToCenter * 3000., float(stateChangeTime));
    float fadeOut = smoothstep(fadeInTime + distToCenter, 0., float(stateChangeTime));
    float fadeMultiplier;
    if (u_state == 0) {
        fadeMultiplier = fadeIn;
    } else if (u_state == 1) {
        fadeMultiplier = 1.;
    } else {
        fadeMultiplier = fadeOut;
    }
    gridSize = mix(20., gridSize, fadeMultiplier);

    float noiseMapR = cnoise(vec3(proportionRelatedPosition * noiseScale, float(u_time)/noiseSpeedMult) + 0.1 * distToCursor) / 2. + 0.5;
    float noiseMapG = cnoise(vec3(proportionRelatedPosition * noiseScale, float(u_time)/noiseSpeedMult) + 0.05 * distToCursor) / 2. + 0.5;
    float noiseMapB = cnoise(vec3(proportionRelatedPosition * noiseScale, float(u_time)/noiseSpeedMult)) / 2. + 0.5;
    vec3 noiseMapRGB = vec3(noiseMapR, noiseMapG, noiseMapB);
    vec4 noiseMapCMYK = RGBtoCMYK(noiseMapRGB);

    float pressureInfluence = u_pointerPressure * (distToCursor * 500.) * pow(smoothstep(1.2, 0.0, distToCursor), 8.);
    float pressureFade = 1. - (u_pointerPressure * pow(smoothstep(1.2, 0.0, distToCursor), 8.));
    currentPixel = currentPixel + normalize(u_cursorPosition - currentPixel) * pressureInfluence;

    vec2 yellowGrid = fract(rotate2D(currentPixel - u_canvasSize / 2., radians(0.) + (float(u_time)/10000.*gridRotationSpeed)) / gridSize);
    vec2 cyanGrid = fract(rotate2D(currentPixel - u_canvasSize / 2., radians(15.) + (float(u_time)/20000.*gridRotationSpeed)) / gridSize);
    vec2 blackGrid = fract(rotate2D(currentPixel - u_canvasSize / 2., radians(45.) + (float(u_time)/40000.*gridRotationSpeed)) / gridSize);
    vec2 magentaGrid = fract(rotate2D(currentPixel - u_canvasSize / 2., radians(75.) + (float(u_time)/80000.*gridRotationSpeed)) / gridSize);

    float cyanCircle = circle(cyanGrid, noiseMapCMYK.x * 2., 1.) * bgBrightnessMult;
    float magentaCircle = circle(magentaGrid, noiseMapCMYK.y * 2., 1.) * bgBrightnessMult;
    float yelloCircle = circle(yellowGrid, noiseMapCMYK.z * 2., 1.) * bgBrightnessMult;
    float blackCircle = circle(blackGrid, mix(2., noiseMapCMYK.w * 2., fadeMultiplier) * pressureFade , mix(0., 1., fadeMultiplier)) * mix(1., bgBrightnessMult, fadeMultiplier);

    vec4 sample = texture2D(u_sampler0, v_position);
    vec4 layoutSample = vec4(1., 1., 1., 0.) - texture2D(u_layoutSampler, v_position);

    vec4 sampleCMYK = RGBtoCMYK(sample.rgb);
    vec4 layoutSampleCMYK = RGBtoCMYK(layoutSample.rgb);

    cyanCircle = min(circle(cyanGrid, sampleCMYK.x * 2. + sampleCMYK.w, 1.), cyanCircle) + circle(cyanGrid, sampleCMYK.x, 1.);
    magentaCircle = min(circle(magentaGrid, sampleCMYK.y * 2. + sampleCMYK.w, 1.), magentaCircle) + circle(magentaGrid, sampleCMYK.y, 1.);
    yelloCircle = min(circle(yellowGrid, sampleCMYK.z * 2. + sampleCMYK.w, 1.), yelloCircle) + circle(yellowGrid, sampleCMYK.z, 1.);
    blackCircle = mix(blackCircle, min(circle(blackGrid, sampleCMYK.w * 2., 1.), blackCircle), fadeMultiplier);

    if (layoutSample.w != 0.) {
        cyanCircle = circle(cyanGrid, layoutSampleCMYK.w * 2., 1.) + cyanCircle;
        magentaCircle = circle(magentaGrid, layoutSampleCMYK.w * 2., 1.) + magentaCircle;
        yelloCircle = circle(yellowGrid, layoutSampleCMYK.w * 2., 1.) + yelloCircle;
        blackCircle = circle(blackGrid, layoutSampleCMYK.w * 2., 1.) + blackCircle;
    }

    vec4 CMYKcolor = vec4(cyanCircle * u_param2, magentaCircle * u_param3, yelloCircle * u_param4, blackCircle * u_param5);
    vec3 RGBcolor = CMYKtoRGB(CMYKcolor);
    RGBcolor = RGBcolor;

    gl_FragColor = vec4(RGBcolor, 1.0);
}
`

export const CirclesCursorFs = `
precision mediump float;
varying vec2 v_position;

uniform int u_state;
uniform int u_time;
uniform int u_anchorTime;

uniform vec2 u_canvasSize;
uniform vec2 u_cursorPosition;
uniform vec2 u_particlePosition;

uniform sampler2D u_sampler0;

uniform float u_param0;
uniform float u_param1;

${PerlinNoise3D}
${CMYK}

vec2 rotate2D(vec2 _st, float _angle){
    _st -= 0.5;
    _st =  mat2(cos(_angle),-sin(_angle),
                sin(_angle),cos(_angle)) * _st;
    _st += 0.5;
    return _st;
}

float circle(in vec2 _st, in float _radius, float _smoothEdges){
    vec2 l = _st-vec2(0.5);
    return 1.-smoothstep(_radius-(_radius*_smoothEdges),
                         _radius,
                         dot(l,l)*4.0);
}

void main() {
    float fadeInTime = 1000.;
    float fadeOutTime = 1000.;
    float bgBrightnessMult = 0.8;
    float noiseScale = 6.;
    float noiseSpeedMult = 3000.;
    
    float gridSize = mix(4., 20., u_param0);
    float gridRotationSpeed = mix(0., 10., u_param1);

    float distToCursor = distance(v_position.xy * u_canvasSize, u_cursorPosition) * 2. / min(u_canvasSize.x, u_canvasSize.y);
    
    vec2 currentPixel = v_position.xy * u_canvasSize;
    float proportion = u_canvasSize.x / u_canvasSize.y;
    vec2 proportionRelatedPosition;
    vec2 centerOffset;
    if (proportion < 1.) {
        proportionRelatedPosition = vec2(v_position.x, v_position.y / proportion);
        centerOffset = vec2(0., (1. - 1. / proportion) / 2.);
    } else {
        proportionRelatedPosition = vec2(v_position.x * proportion, v_position.y);
        centerOffset = vec2((1. - proportion) / 2., 0.);
    }
    
    vec4 sample = texture2D(u_sampler0, v_position);
    float distToCenter = distance(proportionRelatedPosition.xy + centerOffset, vec2(.5, .5));
    float fadeIn = smoothstep(0., fadeInTime + distToCenter * 3000., float(u_time));
    float fadeOut = smoothstep(fadeInTime + distToCenter, 0., float(u_time - u_anchorTime));
    float fadeMultiplier;
    if (u_state == 0 || u_state == 1) {
        fadeMultiplier = fadeIn;
    } else {
        fadeMultiplier = fadeOut;
    }
    gridSize = mix(20., gridSize, fadeMultiplier);

    vec2 blackGrid = fract(rotate2D(currentPixel - u_canvasSize / 2., radians(45.) + (float(u_time) / 40000. * gridRotationSpeed)) / gridSize);

    vec2 rotatedGridBlack = rotate2D(currentPixel - u_canvasSize / 2., radians(45.) + (float(u_time) / 40000.0 * gridRotationSpeed));
    vec2 cellIndexBlack = floor(rotatedGridBlack / gridSize);
    vec2 cellCenterBlack = (cellIndexBlack + vec2(0.5)) * gridSize;
    vec2 screenCellCenterBlack = rotate2D(cellCenterBlack, -radians(45.0) - (float(u_time) / 40000.0 * gridRotationSpeed)) + u_canvasSize / 2.0;
    float distToParticleBlack = distance(screenCellCenterBlack, u_particlePosition);
    float particleBlack = 1. - smoothstep(0., 40. * (gridSize / 6.), distToParticleBlack);
    particleBlack = particleBlack * step(0.2, cnoise(vec3((screenCellCenterBlack - u_particlePosition / 10.) / noiseScale, float(u_time)/noiseSpeedMult)));
    float cursorBlack = 1. - step(gridSize * 1.2, distToParticleBlack);

    float blackCircle = circle(blackGrid, max(particleBlack * 1., cursorBlack * 1.), 0.);

    vec4 CMYKcolor = vec4(0., 0., 0., blackCircle);
    vec3 RGBcolor = CMYKtoRGB(CMYKcolor);
    vec4 color = vec4(RGBcolor, 1.0);
    if (RGBcolor.r + RGBcolor.g + RGBcolor.b > 2.9) {
        color = vec4(0., 0., 0., 0.);
    }

    gl_FragColor = color;
}
`

export const mandelbrotFs = `
precision mediump float;
varying vec2 v_position;

uniform int u_state;
uniform int u_time;
uniform int u_anchorTime;

uniform vec2 u_canvasSize;
uniform vec2 u_cursorPosition;
uniform vec2 u_particlePosition;

uniform int u_audioReactiveOn;
uniform int u_beatDetected;
uniform int u_beatCount;
uniform float u_beatFade;
uniform float u_energy;

uniform float u_param0;
uniform float u_param1;
uniform float u_param2;
uniform float u_param3;
uniform float u_param4;
uniform float u_param5;
uniform float u_param6;

uniform sampler2D u_layoutSampler;

mat2 rotate2d(float _angle){
    return mat2(cos(_angle),-sin(_angle),
                sin(_angle),cos(_angle));
}

const float iter = 1000.;

float calcMandPoint(vec2 pos) {
    vec2 newPos = pos.xy;
    for (float i = 0.; i < iter; i++) {
        newPos = vec2(pow(newPos.x,2.) - pow(newPos.y,2.), 2. * newPos.x * newPos.y) + pos.xy;
        if (length(newPos) > 2.) return i/iter;
    }
    return 1.;
}

vec2 offset = vec2(0.5, 0.5);

float zoom_start = 1.;
vec2 position_start = vec2(375., 0.);

float zoom_01 = 3. * 0.001361468678756015;
vec2 position_01 = vec2(300.7334583973133, -220.70171769685354);

float zoom_02 = 3. * 0.001361468678756015;
vec2 position_02 = vec2(111.32154473915706, -387.325636568428);

float zoom_03 = 3. * 0.001361468678756015;
vec2 position_03 = vec2(116.00168204885264, -379.445150736302);

float zoom_04 = 3. * 0.001361468678756015;
vec2 position_04 = vec2(110.36850853652993, -382.6778558308249);

void main() {

    float proportion = u_canvasSize.x / u_canvasSize.y;
    vec2 proportionRelatedPosition;
    vec2 centerOffset;
    if (proportion < 1.) {
        proportionRelatedPosition = vec2(v_position.x, v_position.y / proportion);
        centerOffset = vec2(0., (1. - 1. / proportion) / 2.);
    } else {
        proportionRelatedPosition = vec2(v_position.x * proportion, v_position.y);
        centerOffset = vec2((1. - proportion) / 2., 0.);
    }
    
    float distToCenter = distance(proportionRelatedPosition.xy + centerOffset, vec2(.5, .5));

    float zoom;
    vec2 position;
    int stateChangeTime = u_time - u_anchorTime;

    float sceneFadeInBrightness = 1.;
    float sceneFadeInSaturation = 1.;
    float sceneFadeInAberration = 1.;
    float sceneFadeInZoomRotation = 1.;

    float locationZoom;
    vec2 locationPosition;
    if (u_param0 < 0.25) {
        locationZoom = zoom_01;
        locationPosition = position_01;
    } else if (u_param0 < 0.5) {
        locationZoom = zoom_02;
        locationPosition = position_02;
    } else if (u_param0 < 0.75) {
        locationZoom = zoom_03;
        locationPosition = position_03;
    } else {
        locationZoom = zoom_04;
        locationPosition = position_04;
    }

    if (u_state == 0) {
        sceneFadeInBrightness = smoothstep(0., 1500., float(stateChangeTime));
        sceneFadeInSaturation = smoothstep(5000., 8000., float(stateChangeTime));
        sceneFadeInAberration = smoothstep(4000., 8000., float(stateChangeTime));
        sceneFadeInZoomRotation = smoothstep(0., 8000., float(stateChangeTime));
        zoom = mix(zoom_start, locationZoom, smoothstep(0., 8000., float(stateChangeTime)) );
        position = mix(position_start, locationPosition, 1. - pow(1. - smoothstep(0., 6000., float(stateChangeTime)), 3.) );
    } else if (u_state == 2) {
        sceneFadeInBrightness = smoothstep(2000., 1000., float(stateChangeTime));
        sceneFadeInSaturation = smoothstep(1000., 500., float(stateChangeTime));
        sceneFadeInAberration = smoothstep(1000., 0., float(stateChangeTime));
        zoom = mix(zoom_start, locationZoom, smoothstep(2000., 0., float(stateChangeTime)) );
        position = locationPosition;
    } else {
        zoom = locationZoom;
        position = locationPosition;
    }

    float brightnessMult = 0.5 * sceneFadeInBrightness;
    vec2 st = vec2(proportionRelatedPosition.xy + centerOffset);
    st -= vec2(0.5);

    float beatSaturation = (u_audioReactiveOn == 1 && float(u_param3) == 1.) ? u_beatFade : 1.0;
    float beatRotation = (u_audioReactiveOn == 1 && float(u_param4) == 1.) ? (float(u_beatCount) - pow(u_beatFade, 5.)) / 5. : 0.;
    float beatAberration = (u_audioReactiveOn == 1 && float(u_param5) == 1.) ? u_beatFade : 1.0;
    float beatZoom = (u_audioReactiveOn == 1 && u_param6 == 1.) ? sin((3.14159265 / 2.0) * (u_beatFade / 0.8)) : 0.0;
    zoom -= beatZoom * distToCenter * 0.001;

    vec4 layoutSample = texture2D(u_layoutSampler, v_position);

    if (u_param2 != 0.) {
        vec2 stR = rotate2d( mix( 0.,mix( mod( (float(u_time + 8000 - stateChangeTime)/10000. + 0.06 * u_param2 * sceneFadeInAberration * beatAberration) + beatRotation , 6.28318530718 )       , mod((float(u_time) / 10000. + 0.06 * u_param2 * sceneFadeInAberration * beatAberration) + beatRotation , 6.28318530718), step(1., sceneFadeInZoomRotation) ), sceneFadeInZoomRotation) ) * st;
        vec2 stG = rotate2d( mix( 0.,mix( mod( (float(u_time + 8000 - stateChangeTime)/10000.) + beatRotation , 6.28318530718 ), mod((float(u_time) / 10000.) + beatRotation , 6.28318530718), step(1., sceneFadeInZoomRotation) ), sceneFadeInZoomRotation) ) * st;
        vec2 stB = rotate2d( mix( 0.,mix( mod( (float(u_time + 8000 - stateChangeTime)/10000. - 0.06 * u_param2 * sceneFadeInAberration * beatAberration) + beatRotation, 6.28318530718 )       , mod((float(u_time) / 10000. - 0.06 * u_param2 * sceneFadeInAberration * beatAberration) + beatRotation , 6.28318530718), step(1., sceneFadeInZoomRotation) ), sceneFadeInZoomRotation) ) * st;
    
        stR += vec2(0.5);
        stG += vec2(0.5);
        stB += vec2(0.5);

        //zoom = zoom / mix(0.3, ((sin(float(u_time) / 8000.) + 1.) * 20. + 1.), pow(sceneFadeInZoomRotation, 2.));

        float zoomR = zoom / mix(0.3, ((sin(float(u_time) / 8000. + 0.02 * u_param2 * sceneFadeInAberration * beatAberration) + 1.) * 20. + 1.), pow(sceneFadeInZoomRotation, 2.));
        float zoomG = zoom / mix(0.3, ((sin(float(u_time) / 8000.) + 1.) * 20. + 1.), pow(sceneFadeInZoomRotation, 2.));
        float zoomB = zoom / mix(0.3, ((sin(float(u_time) / 8000. - 0.02 * u_param2 * sceneFadeInAberration * beatAberration) + 1.) * 20. + 1.), pow(sceneFadeInZoomRotation, 2.));

        stR = vec2((stR.x - offset.x) * zoomR, (stR.y - offset.y) * zoomR);
        stG = vec2((stG.x - offset.x) * zoomG, (stG.y - offset.y) * zoomG);
        stB = vec2((stB.x - offset.x) * zoomB, (stB.y - offset.y) * zoomB);

        stR = stR + vec2(-position.x/500.,position.y/500.);
        stG = stG + vec2(-position.x/500.,position.y/500.);
        stB = stB + vec2(-position.x/500.,position.y/500.);

        float finColorR = calcMandPoint(stR);
        float finColorG = calcMandPoint(stG);
        float finColorB = calcMandPoint(stB);

        finColorR = (sin(finColorR * 100. - float(u_time) / 800. - 1.0 * u_param1 * sceneFadeInSaturation * beatSaturation) + 1.);
        finColorG = (sin(finColorG * 100. - float(u_time) / 800.) + 1.);
        finColorB = (sin(finColorB * 100. - float(u_time) / 800. + 1.0 * u_param1 * sceneFadeInSaturation * beatSaturation) + 1.);
        gl_FragColor = layoutSample.w == 1. ? mix(vec4(1.-finColorR * brightnessMult, 1.-finColorG * brightnessMult, 1.-finColorB * brightnessMult, 1.0), layoutSample, layoutSample.r) : vec4(finColorR * brightnessMult, finColorG * brightnessMult, finColorB * brightnessMult, 1.0);
    } else {
        st = rotate2d( mix( 0.,mix( mod( (float(u_time + 8000 - stateChangeTime)/10000.) + beatRotation , 6.28318530718 ), mod((float(u_time) / 10000.) + beatRotation , 6.28318530718), step(1., sceneFadeInZoomRotation) ), sceneFadeInZoomRotation) ) * st;
        st += vec2(0.5);
        zoom = zoom / mix(0.3, ((sin(float(u_time) / 8000.) + 1.) * 20. + 1.), pow(sceneFadeInZoomRotation, 2.));
        st = vec2((st.x - offset.x) * zoom, (st.y - offset.y) * zoom);
        st = st + vec2(-position.x/500.,position.y/500.);

        float finColor = calcMandPoint(st);
        float finColorR = (sin(finColor * 100. - float(u_time) / 800. - 1.0 * u_param1 * sceneFadeInSaturation * beatSaturation) + 1.);
        float finColorG = (sin(finColor * 100. - float(u_time) / 800.) + 1.);
        float finColorB = (sin(finColor * 100. - float(u_time) / 800. + 1.0 * u_param1 * sceneFadeInSaturation * beatSaturation) + 1.);
        gl_FragColor = layoutSample.w == 1. ? mix( mix( vec4(finColorR * brightnessMult, finColorG * brightnessMult, finColorB * brightnessMult, 1.0), vec4(1.-finColorR * brightnessMult, 1.-finColorG * brightnessMult, 1.-finColorB * brightnessMult, 1.0), layoutSample.r * 2.), layoutSample, layoutSample.r) : vec4(finColorR * brightnessMult, finColorG * brightnessMult, finColorB * brightnessMult, 1.0);
    }
}
`

export const mandelbrotCursorFs = `
precision mediump float;
varying vec2 v_position;

uniform vec2 u_canvasSize;
uniform vec2 u_cursorPosition;
uniform vec2 u_particlePosition;

uniform sampler2D u_sampler0;

void main() {
    float distToParticle = distance(v_position * u_canvasSize, u_particlePosition);
    const float effectRadius = 50.0; // radius of the vortex effect

    if (distToParticle < effectRadius) {
        // Normalize the distance to the particle to range between 0 and 1
        float normalizedDist = clamp(distToParticle / effectRadius, 0.0, 1.0);

        // Calculate angle for rotation (more rotation as closer to particle)
        float angle = pow(smoothstep(0., 1., 1.0 - normalizedDist), 3.) * 2.0 * 3.14159; // full twist closer to the particle

        // Coordinates relative to the particle position
        vec2 relativePos = (v_position * u_canvasSize - u_particlePosition) / effectRadius;

        // Rotate texture coordinates around the particle
        vec2 rotatedPos;
        rotatedPos.x = cos(angle) * relativePos.x - sin(angle) * relativePos.y;
        rotatedPos.y = sin(angle) * relativePos.x + cos(angle) * relativePos.y;

        // Adjust the rotated coordinates back to texture space
        vec2 textureCoords = (rotatedPos * effectRadius + u_particlePosition) / u_canvasSize;

        // Fetch the texture color
        vec4 textureColor = texture2D(u_sampler0, textureCoords);

        // Interpolate the color between black and the texture color
        vec4 finalColor = mix(vec4(0.0, 0.0, 0.0, 1.0), textureColor, smoothstep(0.05, 0.35, normalizedDist));

        gl_FragColor = finalColor;
    }
}
`

export const mirrorFs = `
precision mediump float;
varying vec2 v_position;

uniform int u_state;
uniform int u_time;
uniform int u_anchorTime;

uniform vec2 u_canvasSize;
uniform vec2 u_cursorPosition;
uniform vec2 u_particlePosition;

uniform int u_audioReactiveOn;
uniform int u_beatDetected;
uniform int u_beatCount;
uniform float u_beatFade;
uniform float u_energy;

uniform float u_param0;
uniform float u_param1;
uniform float u_param2;
uniform float u_param3;
uniform float u_param4;
uniform float u_param5;

uniform sampler2D u_sampler0;
uniform sampler2D u_layoutSampler;

float radialFade(float normalizedAngleFromCenter) {
    float angleFade;
    if (normalizedAngleFromCenter < 0.25) {
        angleFade = 1.0 - 4.0 * normalizedAngleFromCenter;
    } else if (normalizedAngleFromCenter < 0.5) {
        angleFade = 4.0 * (normalizedAngleFromCenter - 0.25);
    } else if (normalizedAngleFromCenter < 0.75) {
        angleFade = 1.0 - 4.0 * (normalizedAngleFromCenter - 0.5);
    } else {
        angleFade = 4.0 * (normalizedAngleFromCenter - 0.75);
    }
    return angleFade;
}

vec4 grayscale(vec4 rgba) {
    float grayscale = 0.2126 * rgba.r + 0.7152 * rgba.g + 0.0722 * rgba.b;
    return vec4(grayscale, grayscale, grayscale, rgba.a);
}

vec4 crossProcess(vec4 color) {
    vec3 outputColor;
    outputColor.r = clamp((color.r * 0.7) + (color.g * 0.28), 0.0, 1.0);
    outputColor.g = clamp((color.g * 0.8) + (color.b * 0.2), 0.0, 1.0);
    outputColor.b = clamp((color.r * 0.2) + (color.g * 0.3) + (color.b * 0.5), 0.0, 1.0);
    return vec4(outputColor, color.a);
}

vec4 bloom(vec4 color, float threshold) {
    float intensity = dot(color.rgb, vec3(1.0)) / 3.0; // Average intensity
    vec3 bloom = smoothstep(vec3(threshold), vec3(threshold + 0.1), color.rgb);
    return vec4(mix(color.rgb, bloom, 0.5), color.a);
}

vec4 contrast(vec4 rgba, float contrast) {
    float factor = (259.0 * (contrast + 1.0)) / (255.0 * (1.0 - contrast));

    vec4 adjusted;
    adjusted.r = clamp(factor * (rgba.r - 0.5) + 0.5, 0.0, 1.0);
    adjusted.g = clamp(factor * (rgba.g - 0.5) + 0.5, 0.0, 1.0);
    adjusted.b = clamp(factor * (rgba.b - 0.5) + 0.5, 0.0, 1.0);
    adjusted.a = rgba.a; // Preserve the alpha channel

    return adjusted;
}

float easeOutCirc(float x) {
    return sqrt(1. - pow(x - 1., 2.));
}

void main() {
    float proportion = u_canvasSize.x / u_canvasSize.y;
    vec2 proportionRelatedPosition;
    vec2 centerOffset;
    if (proportion < 1.) {
        proportionRelatedPosition = vec2(v_position.x, v_position.y / proportion);
        centerOffset = vec2(0., (1. - 1. / proportion) / 2.);
    } else {
        proportionRelatedPosition = vec2(v_position.x * proportion, v_position.y);
        centerOffset = vec2((1. - proportion) / 2., 0.);
    }
    
    float distToCenter = distance(proportionRelatedPosition.xy + centerOffset, vec2(.5, .5));

    int stateChangeTime = u_time - u_anchorTime;

    float brightnessMult = 1.;
    if (u_state == 0) {
        brightnessMult = smoothstep(0., 500., float(stateChangeTime));
    } else if (u_state == 2) {
        brightnessMult = smoothstep(500., 0., float(stateChangeTime));
    }

    //float beatSaturation = u_audioReactiveOn == 1 ? u_beatFade : 1.0;
    //float beatRotation = u_audioReactiveOn == 1 ? (float(u_beatCount) - pow(u_beatFade, 5.)) / 5. : 0.;
    //float beatAberration = u_audioReactiveOn == 1 ? u_beatFade : 1.0;
    float beatZoom = u_audioReactiveOn == 1 ? sin((3.14159265 / 2.0) * (u_beatFade / 0.8)) : 0.0;
    
    float sizeMultiplier = mix(6.0, 0.5, u_param3) + beatZoom * ((1. - u_param3) + 0.2) * 1.5;
    float speedMultiplier = mix(0.75 * ((1. - u_param3) + 0.2) * 6., 5.0 * ((1. - u_param3) + 0.2) * 6., u_param4);
    float displacementScale = mix(0.1, 1.2, u_param5) + beatZoom * ((u_param3) + 0.4) * 0.5;
    
    vec2 sampleLocationR, sampleLocationG, sampleLocationB;
    float pi = 3.14159265359;
    
    if (u_param0 < 0.33) {
        float angleFromCenter = atan(proportionRelatedPosition.y + centerOffset.y - 0.5, proportionRelatedPosition.x + centerOffset.x - 0.5);
        float horizontalNormalizedAngleFromCenter = (angleFromCenter + 3.14159265) / (2.0 * 3.14159265);
    
        float verticalNormalizedAngleFromCenter = (horizontalNormalizedAngleFromCenter + 0.25);
        if (verticalNormalizedAngleFromCenter > 1.0) {
            verticalNormalizedAngleFromCenter -= 1.0;
        }
    
        float horizontalAngleFade = radialFade(horizontalNormalizedAngleFromCenter);
        float verticalAngleFade = radialFade(verticalNormalizedAngleFromCenter);

        float horizontalWaveFromCentreR = (sin(distToCenter * 50. * sizeMultiplier - float(u_time) / 800. * speedMultiplier + 0.15 * sizeMultiplier)) / 2. * horizontalAngleFade * smoothstep(0.0, 0.1, distToCenter);
        float horizontalWaveFromCentreG = (sin(distToCenter * 50. * sizeMultiplier - float(u_time) / 800. * speedMultiplier)) / 2. * horizontalAngleFade * smoothstep(0.0, 0.1, distToCenter);
        float horizontalWaveFromCentreB = (sin(distToCenter * 50. * sizeMultiplier - float(u_time) / 800. * speedMultiplier - 0.15 * sizeMultiplier)) / 2. * horizontalAngleFade * smoothstep(0.0, 0.1, distToCenter);
        
        float verticalWaveFromCentreR = (sin(distToCenter * 50. * sizeMultiplier - float(u_time) / 800. * speedMultiplier + 0.15 * sizeMultiplier)) / 2. * verticalAngleFade * smoothstep(0.0, 0.1, distToCenter);
        float verticalWaveFromCentreG = (sin(distToCenter * 50. * sizeMultiplier - float(u_time) / 800. * speedMultiplier)) / 2. * verticalAngleFade * smoothstep(0.0, 0.1, distToCenter);
        float verticalWaveFromCentreB = (sin(distToCenter * 50. * sizeMultiplier - float(u_time) / 800. * speedMultiplier - 0.15 * sizeMultiplier)) / 2. * verticalAngleFade * smoothstep(0.0, 0.1, distToCenter);
        
        sampleLocationR = vec2(v_position.x + horizontalWaveFromCentreR * displacementScale, v_position.y + verticalWaveFromCentreR * displacementScale);
        sampleLocationG = vec2(v_position.x + horizontalWaveFromCentreG * displacementScale, v_position.y + verticalWaveFromCentreG * displacementScale);
        sampleLocationB = vec2(v_position.x + horizontalWaveFromCentreB * displacementScale, v_position.y + verticalWaveFromCentreB * displacementScale);
    } else if (u_param0 < 0.66) {
        float horizontalWaveR = pow( cos( pi * (proportionRelatedPosition.x * 50. * sizeMultiplier - float(u_time) / 800. * speedMultiplier + 0.15 * sizeMultiplier) / 2.) ,2. ) - 0.5;
        float horizontalWaveG = pow( cos( pi * (proportionRelatedPosition.x * 50. * sizeMultiplier - float(u_time) / 800. * speedMultiplier) / 2.) ,2. ) - 0.5;
        float horizontalWaveB = pow( cos( pi * (proportionRelatedPosition.x * 50. * sizeMultiplier - float(u_time) / 800. * speedMultiplier - 0.15 * sizeMultiplier) / 2.) ,2. ) - 0.5;
        
        sampleLocationR = vec2(v_position.x + horizontalWaveR * displacementScale * 0.2, v_position.y);
        sampleLocationG = vec2(v_position.x + horizontalWaveG * displacementScale * 0.2, v_position.y);
        sampleLocationB = vec2(v_position.x + horizontalWaveB * displacementScale * 0.2, v_position.y);
    } else {
        float verticalWaveR = pow( cos( pi * (proportionRelatedPosition.y * 50. * sizeMultiplier - float(u_time) / 800. * speedMultiplier + 0.15 * sizeMultiplier) / 2.) ,2. ) - 0.5;
        float verticalWaveG = pow( cos( pi * (proportionRelatedPosition.y * 50. * sizeMultiplier - float(u_time) / 800. * speedMultiplier) / 2.) ,2. ) - 0.5;
        float verticalWaveB = pow( cos( pi * (proportionRelatedPosition.y * 50. * sizeMultiplier - float(u_time) / 800. * speedMultiplier - 0.15 * sizeMultiplier) / 2.) ,2. ) - 0.5;
        
        sampleLocationR = vec2(v_position.x, v_position.y + verticalWaveR * displacementScale * 0.2);
        sampleLocationG = vec2(v_position.x, v_position.y + verticalWaveG * displacementScale * 0.2);
        sampleLocationB = vec2(v_position.x, v_position.y + verticalWaveB * displacementScale * 0.2);
    }
    
    
    vec4 sampleR = texture2D(u_sampler0, sampleLocationR);
    vec4 sampleG = texture2D(u_sampler0, sampleLocationG);
    vec4 sampleB = texture2D(u_sampler0, sampleLocationB);

    float contrastParameter = mix(-0.1, 0.5, u_param2);
    sampleR = contrast(sampleR, contrastParameter);
    sampleG = contrast(sampleG, contrastParameter);
    sampleB = contrast(sampleB, contrastParameter);

    if (u_param1 < 0.33) {
        sampleR = crossProcess(sampleR);
        sampleG = crossProcess(sampleG);
        sampleB = crossProcess(sampleB);
    } else if (u_param1 < 0.66) {
        sampleR = grayscale(sampleR);
        sampleG = grayscale(sampleG);
        sampleB = grayscale(sampleB);
    } else {
        sampleR = bloom(sampleR, 0.4);
        sampleG = bloom(sampleG, 0.3);
        sampleB = bloom(sampleB, 0.3);
    }

    vec4 layoutSampleR = texture2D(u_layoutSampler, vec2(v_position.x + 0.002, v_position.y));
    vec4 layoutSampleG = texture2D(u_layoutSampler, v_position);
    vec4 layoutSampleB = texture2D(u_layoutSampler, vec2(v_position.x - 0.002, v_position.y));

    vec3 fincolor = max( vec3(layoutSampleR.r, layoutSampleG.g, layoutSampleB.b), vec3(sampleR.r, sampleG.g, sampleB.b) ) ;
    gl_FragColor = vec4(fincolor * brightnessMult, 1.);
}
`

export const matrixFs = `
precision mediump float;
varying vec2 v_position;

uniform int u_state;
uniform int u_time;
uniform int u_anchorTime;

uniform vec2 u_canvasSize;
uniform vec2 u_cursorPosition;
uniform vec2 u_particlePosition;

uniform int u_audioReactiveOn;
uniform int u_beatDetected;
uniform int u_beatCount;
uniform float u_beatFade;
uniform float u_energy;

uniform float u_param0;
uniform float u_param1;
uniform float u_param2;
uniform float u_param3;
uniform float u_param4;
uniform float u_param5;

uniform sampler2D u_sampler0;
uniform sampler2D u_sampler1;
uniform sampler2D u_sampler2;
uniform sampler2D u_sampler3;
uniform sampler2D u_layoutSampler;

${PerlinNoise3D}



float random (vec2 st) {
    return fract(sin(dot(st.xy,vec2(12.9898,78.233)))*43758.5453123);
}

float calculateHighlight (vec2 st, vec2 border, vec2 cell, float highlightProbability, float stateChangeTime) {
    vec2 currentCell = vec2( floor((st.x - border.x) / cell.x) + 50., floor((st.y - border.y) / cell.y));
    float timeMult = floor(float(u_time) / (40. + (random(vec2(currentCell.x, currentCell.x)) * 60.) ) );
    float fadeInMult = 1.;
    if (u_state == 0) {
        float modA = mod( float(u_time) / (40. + (random(vec2(currentCell.x, currentCell.x)) * 60.)), 1. );
        float modB = mod( float(stateChangeTime) / (40. + (random(vec2(currentCell.x, currentCell.x)) * 60.)), 1. );
        float modDif = 0.;
        if (modB > modA) modDif = modB - modA;
        else modDif = 1. - (modA - modB);
        float fadeInTimeMult = floor( float(stateChangeTime) / (40. + (random(vec2(currentCell.x, currentCell.x)) * 60.)) - modDif);
        float fadeInCell = currentCell.y - fadeInTimeMult;
        if (fadeInCell >= -10.) fadeInMult = 0.;
    }

    currentCell.y = mod(currentCell.y - timeMult, 10000.) / 10000.;
    float noise = random(vec2(currentCell.x / 1000., currentCell.y / 1000.));
    float noiseStep = step(noise, highlightProbability);
    return noiseStep * fadeInMult;
}

vec4 grayscale(vec4 rgba) {
    float grayscale = 0.2126 * rgba.r + 0.7152 * rgba.g + 0.0722 * rgba.b;
    return vec4(grayscale, grayscale, grayscale, rgba.a);
}

vec4 contrast(vec4 rgba, float contrast) {
    float factor = (259.0 * (contrast + 1.0)) / (255.0 * (1.0 - contrast));

    vec4 adjusted;
    adjusted.r = clamp(factor * (rgba.r - 0.5) + 0.5, 0.0, 1.0);
    adjusted.g = clamp(factor * (rgba.g - 0.5) + 0.5, 0.0, 1.0);
    adjusted.b = clamp(factor * (rgba.b - 0.5) + 0.5, 0.0, 1.0);
    adjusted.a = rgba.a; // Preserve the alpha channel

    return adjusted;
}

float customEasing(float x) {
    if (x < 0.25)
        return 0.5 - 0.5 * cos(2.0 * 3.14159 * x / 0.5);
    else if (x < 0.75)
        return 1.0;
    else
        return 0.5 - 0.5 * cos(2.0 * 3.14159 * (x - 0.5) / 0.5);
}

void main() {
    float proportion = u_canvasSize.x / u_canvasSize.y;
    vec2 proportionRelatedPosition;
    vec2 centerOffset;
    if (proportion < 1.) {
        proportionRelatedPosition = vec2(v_position.x, v_position.y / proportion);
        centerOffset = vec2(0., (1. - 1. / proportion) / 2.);
    } else {
        proportionRelatedPosition = vec2(v_position.x * proportion, v_position.y);
        centerOffset = vec2((1. - proportion) / 2., 0.);
    }
    
    float distToCenter = distance(proportionRelatedPosition.xy + centerOffset, vec2(.5, .5));

    int stateChangeTime = u_time - u_anchorTime;

    vec2 samplingPosition = v_position;

    if (u_state == 2) {
        float tDist = smoothstep(0., 5000., float(stateChangeTime));
        vec2 center = vec2(0.5, 0.5);
        vec2 toCenter = center - v_position;
        float distToCentre = length(toCenter);
        samplingPosition = v_position + (toCenter * tDist);
    }

    float fadeInMult = 1.;
    float brightnessMult = 1.;
    if (u_state == 0) {
        fadeInMult = smoothstep(0., 1000., float(stateChangeTime)) * smoothstep(0., -1., v_position.y - float(stateChangeTime) / 5000.);
    }

    float cellHeight = u_param0 * 16. + 8.;
    float cellWidth  = cellHeight * 0.6;
    float cols = floor(u_canvasSize.x / cellWidth);
    float rows = floor(u_canvasSize.y / cellHeight);
    float borderX = (u_canvasSize.x - (cols * cellWidth)) / 2.;
    float borderY = (u_canvasSize.y - (rows * cellHeight)) / 2.;
    vec2 border = vec2(borderX, borderY);

    vec2 currentPixel = samplingPosition * u_canvasSize;
    float borderXMult = min(step(borderX, currentPixel.x), 1. - step(u_canvasSize.x - borderX, currentPixel.x));
    float borderYMult = min(step(borderY, currentPixel.y), 1. - step(u_canvasSize.y - borderY, currentPixel.y));
    float borderMult = min(borderXMult, borderYMult);

    vec2 currentCell = vec2( floor((currentPixel.x - border.x) / cellWidth), floor((currentPixel.y - border.y) / cellHeight) );
    vec2 cellCenter = (currentCell * vec2 (cellWidth, cellHeight) + border + vec2 (cellWidth / 2., cellHeight / 2.)) / u_canvasSize;

    float contrastParameter = mix(0.2, 0.8, u_param1);
    
    vec4 lettersSample = texture2D(u_sampler1, samplingPosition);

    vec4 sampleVideo = texture2D(u_sampler0, cellCenter);
    vec4 sampleVideoGray = grayscale(sampleVideo);
    sampleVideoGray = contrast(sampleVideoGray, contrastParameter);
    vec4 video = mix(vec4(0.,0.,0.,1.), lettersSample, sampleVideoGray.r);

    float pixelNoise1 = step(-0.2, cnoise(vec3(cellCenter * 100., float(u_time)/10000.)) );
    float pixelNoise2 = step(-0.2, cnoise(vec3(cellCenter * 800., float(u_time)/5000.)) );
    vec4 sampleSegmentation = texture2D(u_sampler2, cellCenter);
    float bodyFound = sampleSegmentation.g == 1.? 1. : 0.;
    float bodyNotFound = sampleSegmentation.r == 1.? 1. : 0.;
    float bodyFoundNoise = bodyFound * pixelNoise1 * pixelNoise2;
    float bodyNotFoundNoise = bodyNotFound * pixelNoise1 * pixelNoise2;


    float highlightProbability = mix(0.005, bodyFoundNoise == 1.? 0.5 : bodyNotFound == 1.? 0.01 : 0.02, bodyNotFound == 1.? video.g : video.g * 2.);
    const int tailLength = 25;

    float highlight = calculateHighlight(currentPixel, border, vec2(cellWidth, cellHeight), highlightProbability, float(stateChangeTime));
    if (highlight == 0.) {
        for (int i = 0; i < tailLength; i++) {
            float tail = calculateHighlight(vec2(currentPixel.x, currentPixel.y + float(i) * cellHeight), border, vec2 (cellWidth, cellHeight), highlightProbability, float(stateChangeTime));
            if(tail == 1.) {
                highlight = 1. - (float(i) / float(tailLength));
                break;
            }
        }
    }

    vec4 brightVideo = mix(video, vec4(1.), video.g * 2.);
    //video = video.g > 0.5 ? mix(video, brightVideo, video.g * 2. - 1.) : video;
    //video = mix(video, brightVideo, bodyNotFoundNoise == 1.? video.g : bodyFoundNoise == 1.? video.g * 2. * pixelNoise1 * pixelNoise2 : video.g);
    video = mix(video, brightVideo, bodyFoundNoise == 1.? video.g + 0.2: bodyNotFound == 0. && bodyFound == 0. ? video.g : video.g - 0.2);
    video = mix(video, vec4(0., 0., 0., 1.), bodyFoundNoise == 1.? 0. : bodyNotFound == 0. && bodyFound == 0. ? pixelNoise1 * pixelNoise2 * 0.6 - 0.3 : bodyNotFound == 1. ? pixelNoise1 * pixelNoise2 + 0.8 : pixelNoise1 * pixelNoise2 / 2. + 0.5);
    //video = mix(video, vec4(0., 0., 0., 1.), bodyNotFoundNoise == 1.? 1. - video.g: 0.);
    //video = sampleSegmentation.r + sampleSegmentation.g + sampleSegmentation.b == 0.? video : mix(video, vec4(1.) * rainMask, (video.r + video.g + video.b) * 2.);

    float rainMask = lettersSample.g > 0. ? 1. : 0.;
    vec3 rain = highlight > 0.5 ? mix(vec3(0., lettersSample.g * highlight * borderMult, 0.), vec3(1.), lettersSample.g * ((highlight - 0.5) * 2.)) : lettersSample.rgb * highlight * borderMult;
    

    vec4 samplelayout = texture2D(u_layoutSampler, v_position);
    vec4 layout = mix(vec4(0.,0.,0.,1.), lettersSample, samplelayout.r);
    layout = mix(layout, vec4(1.) * rainMask, samplelayout.r * 0.75);
    


    vec4 finColor = highlight == 1.? mix(vec4(1.), vec4(lettersSample), 0.3) * rainMask : vec4(rain, 1.);
    finColor = max(max(finColor, video * fadeInMult), layout);
    //finColor = sampleSegmentation.r == 0.? finColor : mix(finColor, vec4(1.) * finColor.g * rainMask, 1.0);

    if (u_state == 2) {
        vec4 sampleBgLetters = max(finColor, texture2D(u_sampler1, samplingPosition) * 0.3 * customEasing(smoothstep(0., 5000., float(stateChangeTime))) );
        vec4 feedbackSample = texture2D(u_sampler3, v_position);
        vec4 feedback = vec4(0., feedbackSample.g > 0.1 ? feedbackSample.g * 0.98  * smoothstep(0., 2500., float(stateChangeTime)) : 0., 0., 1.);
        gl_FragColor = max(sampleBgLetters, feedback) * smoothstep(4900., 4000., float(stateChangeTime));
    } else {
        gl_FragColor = finColor;
    }
}
`

export const matrixCursorFs = `
precision mediump float;
varying vec2 v_position;

uniform int u_time;
uniform vec2 u_canvasSize;
uniform vec2 u_cursorPosition;
uniform vec2 u_particlePosition;

uniform sampler2D u_sampler0;

uniform float u_param0;

${PerlinNoise3D}

void main() {
    float noiseScale = 6.;
    float noiseSpeedMult = 3000.;

    float cellHeight = u_param0 * 16. + 8.;
    float cellWidth  = cellHeight * 0.6;
    float cols = floor(u_canvasSize.x / cellWidth);
    float rows = floor(u_canvasSize.y / cellHeight);
    float borderX = (u_canvasSize.x - (cols * cellWidth)) / 2.;
    float borderY = (u_canvasSize.y - (rows * cellHeight)) / 2.;
    vec2 border = vec2(borderX, borderY);

    vec2 currentPixel = v_position.xy * u_canvasSize;
    float borderXMult = min(step(borderX, currentPixel.x), 1. - step(u_canvasSize.x - borderX, currentPixel.x));
    float borderYMult = min(step(borderY, currentPixel.y), 1. - step(u_canvasSize.y - borderY, currentPixel.y));
    float borderMult = min(borderXMult, borderYMult);

    vec2 currentCell = vec2( floor((currentPixel.x - border.x) / cellWidth), floor((currentPixel.y - border.y) / cellHeight) );
    vec2 cellCenter = (currentCell * vec2 (cellWidth, cellHeight) + border + vec2 (cellWidth / 2., cellHeight / 2.));
    
    vec4 lettersSample = texture2D(u_sampler0, v_position);

    float distToParticle = distance(cellCenter, u_particlePosition);
    float particle = 1. - smoothstep(0., cellHeight * 5., distToParticle);
    particle = particle * step(0.2, cnoise(vec3((cellCenter - u_particlePosition / 10.) / noiseScale, float(u_time)/noiseSpeedMult)));

    float rainMask = lettersSample.g > 0. ? 1. : 0.;
    float cursor = 1. - step(cellWidth, distToParticle);

    vec4 bp = lettersSample * particle;
    //vec4 bc = lettersSample * cursor;
    vec4 bc = cursor == 1.? mix(vec4(1.), vec4(lettersSample), 0.3) * rainMask: vec4(0.);

    gl_FragColor = max(bp, bc);
}
`

export const alphaChannelFs = `
precision mediump float;
varying vec2 v_position;

uniform sampler2D u_frameBufferSampler;

void main() {
    vec4 sample = texture2D(u_frameBufferSampler, v_position);
    gl_FragColor = vec4(sample.rgb, max(max(sample.r, sample.g), sample.b));
}
`

export const lightGlowOldFs = `
precision mediump float;
varying vec2 v_position;

uniform sampler2D u_frameBufferSampler;

void main() {
    float blurSize = 0.002; // Size of the blur effect
    vec4 sample = texture2D(u_frameBufferSampler, v_position);

    // Determine brightness
    float brightness = dot(sample.rgb, vec3(0.299, 0.587, 0.114));

    vec4 sum = vec4(0.0);
    int count = 0;

    vec4 color = sample;

    // Apply blur to bright areas
    if (brightness < 1.) { // Threshold for brightness
        for (int x = -4; x <= 4; x++) {
            for (int y = -4; y <= 4; y++) {
                vec2 texOffset = vec2(x, y) * blurSize;
                sum += texture2D(u_frameBufferSampler, v_position + texOffset);
                count++;
            }
        }
        color = sum / float(count);
    }

    gl_FragColor = color;
}
`

export const lightGlowFs = `
precision mediump float;
varying vec2 v_position;

uniform vec2 u_canvasSize;
uniform int u_time;

uniform sampler2D u_frameBufferSampler;

float computeBrightness(vec3 color) {
    return dot(color, vec3(0.299, 0.587, 0.114));
}

void main() {
    vec3 originalColor = texture2D(u_frameBufferSampler, v_position).rgb;
    float originalBrightness = computeBrightness(originalColor);

    if (originalBrightness == 1.0) {
        gl_FragColor = vec4(originalColor, 1.0);
        return;
    }

    const int samples = 8;
    float radius = 1.4 * (((sin(float(u_time) / 1000.) + 1.) / 2.) * 0.8 + 0.2);
    vec3 sumColor = vec3(0.0);
    vec3 maxColor = vec3(0.0);
    float sumInfluence = 0.0;
    int countBright = 0;

    for (int i = -samples; i <= samples; i++) {
        for (int j = -samples; j <= samples; j++) {
            vec2 offset = vec2(float(i), float(j)) * (radius / u_canvasSize);
            vec3 sampleColor = texture2D(u_frameBufferSampler, v_position + offset).rgb;
            float sampleBrightness = computeBrightness(sampleColor);

            if (sampleBrightness > 0.6) {
                float influence = smoothstep(0.6, 0.8, sampleBrightness);
                // Calculate weight based on distance (closer pixels have higher weight)
                float distance = length(vec2(float(i), float(j)));
                float weight = 1.0 - distance / (float(samples) * 1.4);
                float finalWeight = influence * weight;

                sumColor += sampleColor * finalWeight;
                sumInfluence += finalWeight;
                countBright++;

                maxColor = max(maxColor, sampleColor);
            }
        }
    }

    if (countBright == 0) {
        gl_FragColor = vec4(originalColor, 1.0);
    } else {
        vec3 averageBrightColor = sumColor / sumInfluence;
        vec3 finalColor = mix(originalColor, averageBrightColor, sumInfluence / float(samples * samples));
        finalColor = min(finalColor, maxColor);
        gl_FragColor = vec4(finalColor, 1.0);
    }
}
`

export const lightGlowAndLayoutFs = `
precision mediump float;
varying vec2 v_position;

uniform vec2 u_canvasSize;
uniform int u_time;

uniform sampler2D u_frameBufferSampler;
uniform sampler2D u_layoutSampler;

float computeBrightness(vec3 color) {
    return dot(color, vec3(0.299, 0.587, 0.114));
}

void main() {
    vec3 originalColor = texture2D(u_frameBufferSampler, v_position).rgb;
    float originalBrightness = computeBrightness(originalColor);

    if (originalBrightness == 1.0) {
        gl_FragColor = vec4(originalColor, 1.0);
        return;
    }

    const int samples = 8;
    float radius = 1.4 * (((sin(float(u_time) / 1000.) + 1.) / 2.) * 0.8 + 0.2);
    vec3 sumColor = vec3(0.0);
    vec3 maxColor = vec3(0.0);
    float sumInfluence = 0.0;
    int countBright = 0;

    for (int i = -samples; i <= samples; i++) {
        for (int j = -samples; j <= samples; j++) {
            vec2 offset = vec2(float(i), float(j)) * (radius / u_canvasSize);
            vec3 sampleColor = texture2D(u_frameBufferSampler, v_position + offset).rgb;
            float sampleBrightness = computeBrightness(sampleColor);

            if (sampleBrightness > 0.6) {
                float influence = smoothstep(0.6, 0.8, sampleBrightness);
                // Calculate weight based on distance (closer pixels have higher weight)
                float distance = length(vec2(float(i), float(j)));
                float weight = 1.0 - distance / (float(samples) * 1.4);
                float finalWeight = influence * weight;

                sumColor += sampleColor * finalWeight;
                sumInfluence += finalWeight;
                countBright++;

                maxColor = max(maxColor, sampleColor);
            }
        }
    }

    vec4 layoutSample = texture2D(u_layoutSampler, vec2(v_position.x, 1. - v_position.y));

    if (countBright == 0) {
        gl_FragColor = vec4(originalColor, 1.0) + layoutSample;
    } else {
        vec3 averageBrightColor = sumColor / sumInfluence;
        vec3 finalColor = mix(originalColor, averageBrightColor, sumInfluence / float(samples * samples));
        finalColor = min(finalColor, maxColor);
        gl_FragColor = vec4(finalColor, 1.0) + layoutSample;
    }
}
`

export const layoutFs = `
precision mediump float;
varying vec2 v_position;

uniform vec2 u_canvasSize;

uniform sampler2D u_frameBufferSampler;
uniform sampler2D u_layoutSampler;

void main() {
    vec4 originalColor = texture2D(u_frameBufferSampler, v_position);
    vec4 layoutSample = texture2D(u_layoutSampler, vec2(v_position.x, 1. - v_position.y));

    gl_FragColor = originalColor + layoutSample;
}
`

/* 3D shaders */

type NoiseType = 'perlin' | 'dithering' | 'sinWave' | 'lineGrid';
type Color = {
    r: number;
    g: number;
    b: number;
}

const perNoise2c = 
`
float n1 = PerlinNoise(st.xy, 0.8, true, 6);
float n2 = PerlinNoise(st.xy, 0.82, true, 7);
float n3 = PerlinNoise(st.xy, 0.85, true, 8);
float n4 = (n1 + n2 + n3) /3.;float t = (directionaLightA - directionaLightB + 1.) / 2.;
float l = (directionaLightA + directionaLightB);
l = 1. - (1. - l) * (1. - l);
if (l < n4) {
    finColor = mix(col1, col2, (t * 1.5 + n4 * 0.5) * 0.65 - 0.10);
    finColor = mix(bgColor, finColor + vec4(0.12,0.12,0.12,0.) * colMult, min(l,1.));
} else {
    finColor = mix(col1, col2, (t * 1.5 + n4 * 0.5) * 0.65 - 0.10);
    finColor = mix(bgColor, finColor, min(l,1.));
}
`

const dithering2c = 
`
float t = (directionaLightA - directionaLightB + 1.) / 2.;
float l = (directionaLightA + directionaLightB);
l = 1. - (1. - l) * (1. - l);
if (dither(1. - pow(1. - l + 0.1, 3.)) == 0.) {
    finColor = mix(col1, col2, (t * 1.5 + dither(1. - pow(1. - t, 3.)) * 0.5) / 2. );
    finColor = mix(bgColor, finColor + vec4(0.2,0.2,0.2,0.) * colMult, l);
} else {
    finColor = mix(col1, col2, (t * 1.5 + dither(1. - pow(1. - t, 3.)) * 0.5) / 2. );
    finColor = mix(bgColor, finColor, l);
}
`

const sinWaves2c = 
`
float grids = sinGrid(vec2 (st.xy));
float t = (directionaLightA - directionaLightB + 1.) / 2.;
float l = (directionaLightA + directionaLightB);
l = 1. - (1. - l) * (1. - l);
if ((l + 0.1 + grids) / 2. > 0.5) {
    finColor = mix(col1, col2, (t * 1.5 + grids * 0.5) * 0.65 - 0.10);
    finColor = mix(bgColor, finColor, l);
} else { 
    finColor = mix(col1, col2, (t * 1.5 + grids * 0.5) * 0.65 - 0.10);
    finColor = mix(bgColor, finColor + vec4(0.2,0.2,0.2,0.) * colMult, l);
}
`

const lineGrid = 
`
float circleGridH = 190.;
float circleGridSizeH = u_canvasSize.y/circleGridH;
float circleGridW = floor(u_canvasSize.x/circleGridSizeH);
float circleGridSizeW = u_canvasSize.x/circleGridW;
vec2 curCell = vec2(floor(st.x/circleGridSizeW + mod(floor(st.y/circleGridSizeH)/2., 1.)), floor(st.y/circleGridSizeH));
vec2 curCellCentre = vec2(curCell.x * circleGridSizeW + circleGridSizeW * 0.5 * floor(mod(curCell.y + 1., 2.)), curCell.y * circleGridSizeH + circleGridSizeH * 0.5);
float circle = distance(st.xy, curCellCentre.xy)/circleGridSizeH;
float gridSize = u_canvasSize.y/110.;
float grid = fract((st.y+st.x)/gridSize);
float t = (directionaLightA - directionaLightB + 1.) / 2.;
float l = (directionaLightA + directionaLightB);l = 1. - (1. - l) * (1. - l);
if ((l + 0.2 + grid) / 2. > 0.5) {
    finColor = mix(col1, col2, (t * 1.6 + floor(t + circle*1.5 - 0.5) * 0.4) / 2. + 0.15);
    finColor = mix(bgColor, finColor, l);
} else {
    finColor = mix(col1, col2, (t * 1.6 + floor(t + circle*1.5 - 0.5) * 0.4) / 2. + 0.15);
    finColor = mix(bgColor, finColor + vec4(0.2,0.2,0.2,0.) * colMult, l);
}
`


export function adaptiveShapeShaderString(noiseType: NoiseType, bgColor: Color, color1: Color, color2: Color) {
    function transformColor(color: Color) {
        return `${color.r}./255., ${color.g}./255., ${color.b}./255., 1.`
    }

    let noiseShaderCode: string;
    switch (noiseType) {
        case "perlin":
            noiseShaderCode = perNoise2c;
            break;
        case "dithering":
            noiseShaderCode = dithering2c;
            break;
        case "sinWave":
            noiseShaderCode = sinWaves2c;
            break;
        case "lineGrid":
            noiseShaderCode = lineGrid;
            break;
    }
    return `
precision mediump float;

varying highp float directionaLightA;
varying highp float directionaLightB;

uniform vec2 u_canvasSize;

//uniform int uHeight;
//uniform int uWidth;

vec4 col1 = vec4(${transformColor(color1)});
vec4 col2 = vec4(${transformColor(color2)});
vec4 bgColor = vec4(${transformColor(bgColor)});

highp float rand(vec2 co, int seed) {
    highp float a = 12.9898 * float(seed);
    highp float b = 78.233 * float(seed);
    highp float c = 43758.5453 * float(seed);
    highp float dt= dot(co.xy ,vec2(a,b));
    highp float sn= mod(dt,3.14);
    return fract(sin(sn) * c);
}

float PerlinNoise (vec2 point, float scale, bool soften, int seed) {
    vec2 uv = fract(point * scale);
    vec2 cellId = floor(point * scale);
    if (soften) {
        uv = uv * uv * (3. - 2. * uv);
    }
    float bl = rand(cellId, seed);
    float br = rand(cellId + vec2 (1.0,0.0), seed);
    float b = mix(bl, br, uv.x);
    float tl = rand(cellId + vec2 (0.0,1.0), seed);
    float tr = rand(cellId + vec2 (1.0,1.0), seed);
    float t = mix(tl, tr, uv.x);return mix(b, t, uv.y);
}

int mat[16];

float indexValue() {
    float m = floor(u_canvasSize.y/600.)*0.5+1.;
    int x = int(mod(gl_FragCoord.x, 4. * m));
    int y = int(mod(gl_FragCoord.y, 4. * m));
    int i = int(floor(float(x)/m) + floor(float(y)/m) * 4.);
    int j;
    for (int k = 0; k < 16; ++k) if (i == k) j = mat[k];
    return float(j) / 16.0;
}

float dither(float color) {
    float closestColor = (color < 0.5) ? 0. : 1.;
    float secondClosestColor = 1. - closestColor;
    float d = indexValue();
    float distance = abs(closestColor - color);
    return (distance < d) ? closestColor : secondClosestColor;
}

float sinGrid(vec2 st) {
    float gridSize = u_canvasSize.y/220.;
    float sinLength = gridSize * 3.;
    float sinHeight = u_canvasSize.y/60.;
    float modX = sin(st.x / sinLength) * sinHeight/2.;
    float gridY = mod(st.y + modX, gridSize)/gridSize;
    float modY = sin(st.y / sinLength) * sinHeight/2.;
    float gridX = mod(st.x + modY, gridSize)/gridSize;
    float grids = mix(gridY, 1. - gridX/2., gridX);
    return grids;
}

float lineGrid(vec2 st) {
    float a = 1.;
    return a;
}

void main() {
    mat[0] = 1;
    mat[1] = 9;
    mat[2] = 3;
    mat[3] = 11;
    mat[4] = 13;
    mat[5] = 5;
    mat[6] = 15;
    mat[7] = 7;
    mat[8] = 4;
    mat[9] = 12;
    mat[10] = 2;
    mat[11] = 10;
    mat[12] = 15;
    mat[13] = 8;
    mat[14] = 14;
    mat[15] = 6;
    
    vec3 st = vec3(gl_FragCoord.x, gl_FragCoord.y, 0.0);
    float colMult = 1.;
    
    if ((bgColor.x + bgColor.y + bgColor.z)/3. < 0.5) {
        colMult = -1.;
    }

    vec4 finColor;
    ${noiseShaderCode}
    gl_FragColor = vec4(finColor);
}
`;
}

export const wireframeEbbflow = `
precision highp float;
varying highp vec3 v_texCoord;

uniform int u_state;
uniform int u_time;
uniform int u_anchorTime;

float map(float value, float min1, float max1, float min2, float max2) {
    float perc = (value - min1) / (max1 - min1);
    return perc * (max2 - min2) + min2;
}

float easingFunction(float x) {
    if (x < 0.8) {
        // Calculate the upside-down parabolic part with the extremum at 2
        return -25.0/8.0 * (x - 0.8) * (x - 0.8) + 2.0;
    } else {
        // Adjusted sinusoidal part to fit the wave correctly
        return 1.5 + 0.5 * cos(3.14159265 * (x - 0.8) / 0.2);
    }
}

void main() {
    int stateChangeTime = u_time - u_anchorTime;
    float brightnessCoef = 1.;
    float timeFade = smoothstep(-0.5, 0., sin(float(u_time) / 3000.));
    if (u_state == 0) {
        float fadeInCoef = map(float(stateChangeTime), 0., 5000., 0., 1.);
        brightnessCoef = easingFunction(fadeInCoef);
        timeFade = mix(1., timeFade, float(stateChangeTime) / 5000.);
    }
    if (u_state == 2) {
        float fadeOutCoef = map(float(stateChangeTime), 0., 3000., 0., 1.);
        brightnessCoef = 1. - pow(fadeOutCoef, 3.);
    }
    float edgeWidth = 0.03;
    float minSide = min(v_texCoord.x, v_texCoord.y);
    float maxSide = min(1. - v_texCoord.x, 1. - v_texCoord.y);
    float edgeIntensity = min(minSide, maxSide);
    edgeIntensity = 1.0 - smoothstep(0.02, edgeWidth, edgeIntensity);
    if (edgeIntensity > 0.0) {  // Adjust the threshold to ensure visibility
        gl_FragColor = vec4(vec3(edgeIntensity), 0.3 * timeFade * brightnessCoef);
    } else {
        gl_FragColor = vec4(0.0, 0.0, 0.0, 0.0);
    }
}
`

export const wireframeBoxes = `
precision highp float;
varying vec4 v_globalPosition;
varying highp vec3 v_texCoord;

uniform int u_state;
uniform int u_time;
uniform int u_anchorTime;

float map(float value, float min1, float max1, float min2, float max2) {
    float perc = (value - min1) / (max1 - min1);
    return perc * (max2 - min2) + min2;
}

void main() {
    int stateChangeTime = u_time - u_anchorTime;
    float movementCoef = 0.;
    if (u_state == 2) {
        float fadeOutCoef = map(float(stateChangeTime), 0., 3000., 0., 1.);
        movementCoef = pow(fadeOutCoef, 2.) * 4000.;
    }
    float strokeBrightness = map(v_globalPosition.z, 0., 4000. - movementCoef, 1., 0.);
    strokeBrightness = strokeBrightness * min(map(v_globalPosition.z, 200., 400., 0., 1.), 1.);
    float edgeWidth = 0.03;  // Reduced width for more precise wireframe
    float minSide = min(v_texCoord.x, v_texCoord.y);
    float maxSide = min(1. - v_texCoord.x, 1. - v_texCoord.y);
    float edgeIntensity = min(minSide, maxSide);
    edgeIntensity = 1.0 - smoothstep(0.02, edgeWidth, edgeIntensity);
    if (edgeIntensity > 0.0) {  // Adjust the threshold to ensure visibility
        gl_FragColor = vec4(vec3(strokeBrightness), 1.);
    } else {
        gl_FragColor = vec4(0.0, 0.0, 0.0, 1.0);
    }
}
`

export const wireframeCursorBox = `
precision highp float;
varying highp vec3 v_texCoord;

void main() {
    float edgeWidth = 0.15;  // Reduced width for more precise wireframe
    float minSide = min(v_texCoord.x, v_texCoord.y);
    float maxSide = min(1. - v_texCoord.x, 1. - v_texCoord.y);
    float edgeIntensity = min(minSide, maxSide);
    edgeIntensity = 1.0 - smoothstep(0.02, edgeWidth, edgeIntensity);
    if (edgeIntensity > 0.0) {  // Adjust the threshold to ensure visibility
        gl_FragColor = vec4(1.0, 0.0, 0.0, 1.);
    } else {
        gl_FragColor = vec4(0.0, 1.0, 0.0, 1.0);
    }
}
`

export const wireframeCursorEbbflow = `
precision highp float;
varying highp vec3 v_texCoord;

void main() {
    float edgeWidth = 0.15;  // Reduced width for more precise wireframe
    float minSide = min(v_texCoord.x, v_texCoord.y);
    float maxSide = min(1. - v_texCoord.x, 1. - v_texCoord.y);
    float edgeIntensity = min(minSide, maxSide);
    edgeIntensity = 1.0 - smoothstep(0.02, edgeWidth, edgeIntensity);
    if (edgeIntensity > 0.0) {  // Adjust the threshold to ensure visibility
        gl_FragColor = vec4(1.0, 0.0, 0.0, 1.);
    } else {
        gl_FragColor = vec4(0.0, 1.0, 0.0, 1.0);
    }
}
`

export const wireframeCursorBoxPostProcessing = `
precision highp float;

varying vec2 v_position;

uniform vec2 u_canvasSize;
uniform vec2 u_particlePosition;

uniform sampler2D u_frameBufferSampler;

void main() {
    vec2 cursorPosition = v_position + (vec2(u_canvasSize.x - u_particlePosition.x, u_particlePosition.y) / u_canvasSize - 0.5);
    vec4 originalColor = texture2D(u_frameBufferSampler, cursorPosition);
    
    if (originalColor.r + originalColor.g == 0.0) {
        originalColor = vec4(0.0);
    } else if (originalColor.r == 1.0) {
        originalColor = vec4(0.0, 0.0, 0.0, 1.0);
    } else {
        originalColor = vec4(1.0); // White with full opacity
    }
    
    gl_FragColor = originalColor;
}
`

export const wireframeCursorEbbflowPostProcessing = `
precision highp float;

varying vec2 v_position;

uniform vec2 u_canvasSize;
uniform vec2 u_particlePosition;

uniform sampler2D u_frameBufferSampler;

void main() {
    vec2 cursorPosition = v_position + (vec2(u_canvasSize.x - u_particlePosition.x, u_particlePosition.y) / u_canvasSize - 0.5);
    vec4 originalColor = texture2D(u_frameBufferSampler, cursorPosition);
    
    if (originalColor.r + originalColor.g == 0.0) {
        originalColor = vec4(0.0);
    } else if (originalColor.r == 1.0) {
        originalColor = vec4(1.0);
    } else {
        originalColor = vec4(0.0, 0.0, 0.0, 1.0);
    }
    
    gl_FragColor = originalColor;
}
`


export const wireframeMainBox = `
precision mediump float;
varying vec4 v_globalPosition;
varying highp vec3 v_texCoord;

uniform int u_time;
uniform float u_colCoef;

uniform int u_audioReactiveOn;
uniform float u_beatFade;


float NoiseWSeed (vec2 point, float seed, float randomness) {
    return fract(sin(mod((point.x * seed * 100.0 + point.y * seed * randomness * 100.0), 90000.)) * 5000.0);
}

float PerlinNoise (vec2 point, float scale, float pixelization, float soften, float seed, float randomness) {
    vec2 uv = fract(point * scale);
    vec2 cellId = floor(point * scale);

    if (soften == 1.) {uv = uv * uv * (3. - 2. * uv);}

    float bl = NoiseWSeed(cellId, seed, randomness);
    float br = NoiseWSeed(cellId + vec2 (1.0,0.0), seed, randomness);
    float b = mix(bl, br, uv.x);

    float tl = NoiseWSeed(cellId + vec2 (0.0,1.0), seed, randomness);
    float tr = NoiseWSeed(cellId + vec2 (1.0,1.0), seed, randomness);
    float t = mix(tl, tr, uv.x);

    return mix(b, t, uv.y);
}

float u_pixelization = 30.0;
float u_seed = 3.;
float u_randomness = 20.;
float u_perlinScale = 40.;
float u_nSmooth = 1.;
int u_nOctaves = 1;
vec3 u_color0 = vec3(0.,0.,0.);
vec3 u_color1 = vec3(0.25,0.25,0.25);
vec3 u_color2 = vec3(0.8,0.8,0.8);
vec3 u_color3 = vec3(1.0,1.0,1.0);
float u_nColors = 4.;


void main() {
    vec2 coord = v_texCoord.xy;
    float c;

    c = PerlinNoise(floor((coord) * u_pixelization) / u_pixelization + float(u_time)/100000., u_perlinScale, u_pixelization, u_nSmooth, u_seed, u_randomness);
    if (u_nOctaves > 1) {
        c += PerlinNoise(floor(coord * u_pixelization) / u_pixelization, u_perlinScale * 2., u_pixelization, u_nSmooth, u_seed, u_randomness) * 0.5;
        c /= 1.5;
    }
    if (u_nOctaves > 2) {
        c += PerlinNoise(floor(coord * u_pixelization) / u_pixelization, u_perlinScale * 4., u_pixelization, u_nSmooth, u_seed, u_randomness) * 0.25;
        c /= 1.25;
    }
    if (u_nOctaves > 3) {
        c += PerlinNoise(floor(coord * u_pixelization) / u_pixelization, u_perlinScale * 4., u_pixelization, u_nSmooth, u_seed, u_randomness) * 0.125;
        c /= 1.125;
    }

    vec3 fin;
    if (c < 0.33) {fin = mix(u_color0, u_color1, c * 3.);}
    else if (c < 0.66) {fin = mix(u_color1, u_color2, (c - 0.33) * 3.);}
    else {fin = mix(u_color2, u_color3, (c - 0.66) * 3.);}

    float beatMult = u_audioReactiveOn == 1 ? 1. - (sin((3.14159265 / 2.0) * (u_beatFade / 0.8))) : 1.0;

    float edgeWidth = 0.015;  // Reduced width for more precise wireframe
    float minSide = min(v_texCoord.x, v_texCoord.y);
    float maxSide = min(1. - v_texCoord.x, 1. - v_texCoord.y);
    float edgeIntensity = min(minSide, maxSide);
    edgeIntensity = 1.0 - step(edgeWidth, edgeIntensity);
    if (edgeIntensity > 0.0) {  // Adjust the threshold to ensure visibility
        gl_FragColor = vec4(0., 0., 0., 1.);
    } else {
        gl_FragColor = vec4(1.0, 1.0, 1.0, c * 1.5 - 0.5 * beatMult);
        //gl_FragColor = vec4(1.0, 1.0, 1.0, c * u_colCoef);
    }
}
`;

export const solidColor = (color: Color) => {
    function transformColor(color: Color) {
        return `${color.r}./255., ${color.g}./255., ${color.b}./255., 1.`
    }

    return `
void main() {
    gl_FragColor = vec4(${transformColor(color)});
}
`
}

export const frequencyVisualizerTexture = `
precision highp float;

varying highp vec3 v_texCoord;

uniform vec2 u_canvasSize;
uniform int u_state;
uniform int u_time;
uniform int u_anchorTime;

uniform sampler2D u_sampler0;

float map(float value, float min1, float max1, float min2, float max2) {
    float perc = (value - min1) / (max1 - min1);
    return perc * (max2 - min2) + min2;
}

void main() {
    int stateChangeTime = u_time - u_anchorTime;
    float brightnessCoef = 1.;
    if (u_state == 0) {
        float fadeInCoef = map(float(stateChangeTime), 0., 3000., 0., 1.);
        brightnessCoef = pow(fadeInCoef, 3.);
    }
    if (u_state == 2) {
        float fadeOutCoef = map(float(stateChangeTime), 0., 3000., 0., 1.);
        brightnessCoef = pow(1. - fadeOutCoef, 3.);
    }
    vec2 newPosition = vec2(v_texCoord.x, 1. - v_texCoord.y);
    float proportion = u_canvasSize.x / u_canvasSize.y;
    vec2 proportionRelatedPosition;
    vec2 centerOffset;

    proportionRelatedPosition = vec2(newPosition.x / proportion, newPosition.y);
    centerOffset = vec2((1. - 1. / proportion) / 2., 0.);

    vec4 sampler = texture2D(u_sampler0, proportionRelatedPosition + centerOffset);
    gl_FragColor = vec4(sampler.xyz, sampler.w * brightnessCoef);
}
`

export const linesOverlay = `
precision highp float;
varying highp vec4 v_globalPosition;

uniform int u_state;
uniform int u_time;
uniform int u_anchorTime;

float map(float value, float min1, float max1, float min2, float max2) {
    float perc = (value - min1) / (max1 - min1);
    return perc * (max2 - min2) + min2;
}

${PerlinNoise3D}

float noiseLines(float lineTravelDistance, float lineDencity, float lineThickness, float movementCoef) {
    float timeMovement = sin(float(u_time) / 2000.) * lineTravelDistance;
    float yPosition = (v_globalPosition.y + timeMovement + movementCoef * 1000. - v_globalPosition.x / 2. + sin(v_globalPosition.z / 100.) * 100.) / 2.;
    yPosition = yPosition - mod(yPosition, lineThickness);
    vec3 noisePosition = vec3(yPosition, yPosition, yPosition);
    float noise = cnoise(noisePosition);
    float lines = step(lineDencity, noise);
    return lines;
}

void main() {
    int stateChangeTime = u_time - u_anchorTime;
    float movementCoef = 0.;
    float brightnessCoef = 1.;
    if (u_state == 0) {
        float movementFadeInCoef = map(float(stateChangeTime), 0., 5000., 0., 1.);
        float brightnessFadeInCoef = map(float(stateChangeTime), 0., 3000., 0., 1.);
        movementCoef = pow(1. - movementFadeInCoef, 3.);
        brightnessCoef = pow(brightnessFadeInCoef, 3.);
    }
    if (u_state == 2) {
        float fadeOutCoef = map(float(stateChangeTime), 0., 3000., 0., 1.);
        movementCoef = pow(fadeOutCoef, 3.);
        brightnessCoef = pow(1. - fadeOutCoef, 3.);
    }

    float noise1 = noiseLines(120., 0.2, 0.9, movementCoef);
    float noise2 = noiseLines(40., 0.35, 0.35, movementCoef);
    float noise3 = noiseLines(44., 0.32, 0.35, movementCoef);
    float finNoiseLine = max(max(noise1, noise2), noise3);
    gl_FragColor = vec4(vec3(finNoiseLine), finNoiseLine * (((sin(float(u_time) / 1000.) + 1.) / 2.) * 0.1 + 0.9) * brightnessCoef );
}
`

export const cursorLinesOverlay = `
precision highp float;
varying highp vec4 v_localPosition;

uniform int u_time;

${PerlinNoise3D}

float noiseLines(float lineTravelDistance, float lineDencity, float lineThickness) {
    float timeMovement = sin(float(u_time) / 2000.) * lineTravelDistance;
    float yPosition = (v_localPosition.y + timeMovement - v_localPosition.x / 2. + sin(v_localPosition.z / 100.) * 100.) / 2.;
    yPosition = yPosition - mod(yPosition, lineThickness);
    vec3 noisePosition = vec3(yPosition, yPosition, yPosition);
    float noise = cnoise(noisePosition);
    float lines = step(lineDencity, noise);
    return lines;
}

void main() {
    float noise1 = noiseLines(25., 0.04, 1.2);
    float noise2 = noiseLines(10., 0.005, 1.8);
    float finNoiseLine = max(noise1, noise2);

    if (finNoiseLine > 0.0) {  // Adjust the threshold to ensure visibility
        gl_FragColor = vec4(1.0, 0.0, 0.0, 1.);
    } else {
        gl_FragColor = vec4(0.0, 1.0, 0.0, 1.0);
    }
}
`